import { createSlice, configureStore, createAsyncThunk, current } from "@reduxjs/toolkit";
import { merge, mergeWith } from "lodash";
import { findKeyValueInData, replaceArrays } from "@/utils/lenderUtils";
import { reusableFetch, reusableFormDataFetch, reusableWithoutTokenFetch, isFetchResult } from "@/utils/useFetch";
import { LENDER_URL } from "../../socket";
import {
  getAssetTarget,
  handleAssetLoaderState,
  handleAssetFieldsListState,
  handleGlassesFieldsListState,
} from "../../utils/reduxHelpers";
import { COMMERCIAL_CREDIT_CHECKS, DEFAULT_EXPANDED_ASSET_SECTIONS } from "../../constants";

export const startApplication = createAsyncThunk(
  "application/startApplication",
  async ({ url }, { rejectWithValue }) => {
    const newApplication = await reusableFetch(url, "GET", null, rejectWithValue);
    return newApplication;
  },
);

export const getApplication = createAsyncThunk(
  "userDetails/getApplication",
  async (applicationId, { getState, rejectWithValue }) => {
    const application = await reusableFetch(`application/${applicationId}`, "GET", null, rejectWithValue);
    return application;
  },
);

export const getApplicationComments = createAsyncThunk(
  "/getApplicationComments",
  async (applicationId, { getState, rejectWithValue }) => {
    const comments = await reusableFetch(`application/${applicationId}/comment`, "GET", null, rejectWithValue);
    return comments;
  },
);

export const addApplicationComment = createAsyncThunk("applicationComment/add", async (data, { rejectWithValue }) => {
  const comment = await reusableFetch(`application/${data.applicationId}/comment`, "POST", data, rejectWithValue);
  return comment;
});

export const getApplicationActivityLog = createAsyncThunk(
  "/activity-log",
  async (applicationId, { getState, rejectWithValue }) => {
    const application = await reusableFetch(`activity-log/${applicationId}`, "GET", null, rejectWithValue);
    return application;
  },
);

export const getApplicationServiceLog = createAsyncThunk(
  "application/getApplicationServiceLog",
  async (applicationId, { getState, rejectWithValue }) => {
    const serviceLog = await reusableFetch(
      `service-usage/get-service-usage-details?applicationId=${applicationId}`,
      "GET",
      null,
      rejectWithValue,
    );
    return serviceLog?.data;
  },
);

export const updateApplication = createAsyncThunk(
  "application/updateApplication",
  async (data, { rejectWithValue, getState }) => {
    const application = await reusableFetch(`application/update/${data?._id}`, "POST", data, rejectWithValue);
    return application;
  },
);

export const updateApplicationField = createAsyncThunk(
  "application/updateApplicationField",
  async (data, { rejectWithValue }) => {
    const application = await reusableFetch(`application/update/${data?._id}`, "PATCH", data, rejectWithValue);
    return application;
  },
);

export const updateApplicationStatus = createAsyncThunk("application/status", async (data, { rejectWithValue }) => {
  const application = await reusableFetch(
    `application/update/${data.applicationId}/status`,
    "POST",
    data,
    rejectWithValue,
  );
  return application;
});

export const updateApplicationType = createAsyncThunk("application/type", async (data, { rejectWithValue }) => {
  const application = await reusableFetch(
    `application/update/${data.applicationId}/type`,
    "PUT",
    data,
    rejectWithValue,
  );
  return application;
});

export const updateAppStatusToWorkShop = createAsyncThunk("application/", async (data, { rejectWithValue }) => {
  const application = await reusableFetch(`application/update/quote/status`, "PUT", data, rejectWithValue);
  return application;
});

export const submitApplication = createAsyncThunk(
  "application/submitApplication",
  async (data, { rejectWithValue, getState }) => {
    const { browserType = "LOCAL", ...payload } = data || {};
    const application = await reusableFetch(
      `application/submit?browserType=${browserType}`,
      "POST",
      payload,
      rejectWithValue,
    );
    return application;
  },
);

export const submitQuote = createAsyncThunk("application/submitQuote", async (data, { rejectWithValue }) => {
  const quote = await reusableFetch("quote/realtime", "POST", data, rejectWithValue);
  return quote;
});

export const submitApplicationToEmail = createAsyncThunk(
  "application/submit-notify",
  async ({ applicationId }, { rejectWithValue }) => {
    const application = await reusableFetch(`application/submit/notify`, "POST", { applicationId }, rejectWithValue);
    return application;
  },
);

export const set2faCode = createAsyncThunk(
  "application/set2faCode",
  async ({ applicationId, lenderType, code, lenderName }, { rejectWithValue }) => {
    const response = await fetch(`${LENDER_URL}/api/${lenderName.includes("angle") ? "angle" : lenderName}/2fa`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applicationId, lenderType, code }),
    });
    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }
    return { data, status: response.status };
  },
);

export const getApplicationDownloadUrl = createAsyncThunk("application/url", async (data, { rejectWithValue }) => {
  const application = await reusableFetch(`application/download/${data?.applicationId}`, "POST", data, rejectWithValue);
  return application;
});

export const updateAsset = createAsyncThunk("asset/updateAsset", async (data, { rejectWithValue }) => {
  const asset = await reusableFetch(`asset/`, "POST", data, rejectWithValue);
  return asset.data;
});

export const addAdditionalAsset = createAsyncThunk("asset/addAdditionalAsset", async (data, { rejectWithValue }) => {
  const addAdditionalAsset = await reusableFetch(`asset/additionalAsset`, "POST", data, rejectWithValue);
  return addAdditionalAsset;
});

export const removeAdditionalAsset = createAsyncThunk(
  "asset/removeAdditionalAsset",
  async (data, { rejectWithValue }) => {
    const removeAdditionalAsset = await reusableFetch(`asset/additionalAsset`, "Delete", data, rejectWithValue);
    return removeAdditionalAsset;
  },
);

export const getCloneAsset = createAsyncThunk("asset/getCloneAsset", async (data, { rejectWithValue }) => {
  const getCloneAsset = await reusableFetch(`asset/cloneAsset`, "POST", data, rejectWithValue);
  return getCloneAsset;
});

export const makeAssetPrimary = createAsyncThunk("asset/makeAssetPrimary", async (data, { rejectWithValue }) => {
  const reorderAssetList = await reusableFetch(`asset/makeAssetPrimary`, "PUT", data, rejectWithValue);
  return reorderAssetList;
});

export const updateLoanDetails = createAsyncThunk("loanDetails/update", async (data, { rejectWithValue }) => {
  const loanDetails = await reusableFetch(`loanDetails/`, "PATCH", data, rejectWithValue);
  return loanDetails;
});

export const createEntity = createAsyncThunk("entity/create", async (data, { rejectWithValue }) => {
  const application = await reusableFetch(`entity/`, "POST", data, rejectWithValue);
  return application;
});

export const createEmployer = createAsyncThunk("employer/create", async ({ data }, { rejectWithValue }) => {
  const application = await reusableFetch(`employer/`, "POST", data, rejectWithValue);
  return application;
});

export const saveEmployer = createAsyncThunk("employer/save", async ({ data }, { rejectWithValue }) => {
  const application = await reusableFetch(`employer/`, "PUT", data, rejectWithValue);
  return application;
});

export const getEntity = createAsyncThunk("entity/get", async (entityId, { rejectWithValue, getState }) => {
  const entity = await reusableFetch(`entity/${entityId}`, "GET", null, rejectWithValue);
  return entity;
});

export const removeEntity = createAsyncThunk(
  "entity/delete",
  async ({ entityId, applicationId }, { rejectWithValue }) => {
    const entity = await reusableFetch(
      `entity/?entityId=${entityId}&applicationId=${applicationId}`,
      "DELETE",
      {},
      rejectWithValue,
    );
    return entity;
  },
);

export const getASICData = createAsyncThunk("entity/asic-data", async (data, { rejectWithValue }) => {
  const entity = await reusableFetch(`entity/asic-data`, "POST", data, rejectWithValue);
  // A failure is handed back as is, so it rejects rather than fulfilling with nothing.
  return isFetchResult(entity) ? entity.data : entity;
});

const COMMERCIAL_CREDIT_CHECK_URLS = {
  [COMMERCIAL_CREDIT_CHECKS.SCORE]: "entity/credit-score",
  [COMMERCIAL_CREDIT_CHECKS.REPORT]: "entity/credit-report",
};

/** Runs a paid CreditorWatch check. The server attaches the PDF it returns to the entity and responds with it. */
export const getEntityCommercialCreditCheck = createAsyncThunk(
  "entity/commercial-credit-check",
  async ({ check, applicationId, entityId, acn }, { rejectWithValue }) => {
    const document = await reusableFetch(
      COMMERCIAL_CREDIT_CHECK_URLS[check],
      "POST",
      { applicationId, entityId, acn },
      rejectWithValue,
    );
    return document;
  },
);

export const requestEmploymentIncomeVerification = createAsyncThunk(
  "customer/employment-income-verification",
  async ({ applicationId, customerId, months }, { rejectWithValue }) => {
    const verification = await reusableFetch(
      "employment-income-verification",
      "POST",
      { applicationId, customerId, months },
      rejectWithValue,
    );
    return verification;
  },
);

export const updateEntity = createAsyncThunk("entity/update", async (data, { rejectWithValue }) => {
  const entity = await reusableFetch(`entity`, "PUT", data, rejectWithValue);
  return entity;
});

export const getCompanyProfile = createAsyncThunk("entity/update", async ({ ACN }, { rejectWithValue }) => {
  const entity = await reusableFetch(`entity/search?acn=${ACN}`, "GET", null, rejectWithValue);
  return entity;
});

export const createCustomer = createAsyncThunk("customer/create", async (data, { rejectWithValue }) => {
  const newCustomer = await reusableFetch(`customer`, "POST", data, rejectWithValue);
  return newCustomer;
});

export const updateCustomer = createAsyncThunk("customer/update", async ({ data }, { rejectWithValue }) => {
  const customer = await reusableFetch(`customer/`, "PUT", data, rejectWithValue);
  return customer;
});

export const removeCustomer = createAsyncThunk("customer/remove", async (data, { rejectWithValue }) => {
  const customer = await reusableFetch(`customer/remove`, "PUT", data, rejectWithValue);
  return customer;
});

export const startVerification = createAsyncThunk(
  "identity-verification/start",
  async ({ data }, { rejectWithValue }) => {
    const newCustomer = await reusableFetch(`identity-verification/send`, "POST", data, rejectWithValue);
    return newCustomer;
  },
);

export const getIdentityVerification = createAsyncThunk(
  "identity-verification/get",
  async ({ data }, { rejectWithValue, getState }) => {
    const customer = await reusableFetch(`identity-verification/search`, "POST", data, rejectWithValue);
    return customer;
  },
);

export const getDocuments = createAsyncThunk("document/get", async ({ data }, { rejectWithValue }) => {
  const document = await reusableFetch(`document/${data}`, "GET", null, rejectWithValue);
  return document;
});

export const deleteDocument = createAsyncThunk("document/delete", async (data, { rejectWithValue }) => {
  const document = await reusableFetch(`document`, "DELETE", data, rejectWithValue);
  return document;
});

export const getFileUrl = createAsyncThunk("document/file", async (data, { rejectWithValue }) => {
  const url = await reusableFetch(`document/file`, "POST", data, rejectWithValue);
  return url;
});

export const getCreditFile = createAsyncThunk("credit-profile/file", async ({ data }, { rejectWithValue }) => {
  const file = await reusableFetch(`credit-profile/file`, "POST", data, rejectWithValue);
  return file;
});

export const getCommercialCreditFile = createAsyncThunk(
  "credit-profile/file-commercial",
  async (data, { rejectWithValue }) => {
    const file = await reusableFetch(`credit-profile/file-commercial`, "POST", data, rejectWithValue);
    return file;
  },
);

export const getCreditProfile = createAsyncThunk(
  "credit-profile/get",
  async ({ customerId, applicationId }, { rejectWithValue, getState }) => {
    const file = await reusableFetch(`credit-profile/?cid=${customerId}&aid=${applicationId}`, "POST", rejectWithValue);
    return file;
  },
);

export const sendBankStatementsLink = createAsyncThunk("bankStatements/link", async ({ data }, { rejectWithValue }) => {
  const privacy = await reusableFetch(`bank-statements/link`, "POST", data, rejectWithValue);
  return privacy;
});

export const getAddress = createAsyncThunk("address/get", async (addressId, { rejectWithValue }) => {
  const address = await reusableFetch(`address/${addressId}`, "GET", null, rejectWithValue);
  return address;
});

export const addAddress = createAsyncThunk("address/add", async ({ data }, { rejectWithValue }) => {
  const address = await reusableFetch(`address`, "POST", data, rejectWithValue);
  return address;
});

export const updateAddress = createAsyncThunk("address/update", async ({ data }, { rejectWithValue }) => {
  const address = await reusableFetch(`address`, "PUT", data, rejectWithValue);
  return address.data;
});

export const deleteAddress = createAsyncThunk("address/delete", async ({ data }, { rejectWithValue }) => {
  const address = await reusableFetch(`address`, "DELETE", data, rejectWithValue);
  return address?.data;
});

export const addPhoneNumber = createAsyncThunk("phoneNumber/add", async ({ data }, { rejectWithValue, getState }) => {
  const customer = await reusableFetch(`phone-number`, "POST", data, rejectWithValue);
  return customer;
});

export const addEmail = createAsyncThunk("email/add", async ({ data }, { rejectWithValue }) => {
  const customer = await reusableFetch(`email`, "POST", data, rejectWithValue);
  return customer;
});

export const saveCustomerProfile = createAsyncThunk("customerProfile/add", async ({ data }, { rejectWithValue }) => {
  const customerProfile = await reusableFetch(`customer-profile`, "POST", data, rejectWithValue);
  return customerProfile;
});

export const resetExpensesAndLiabilitiesShares = createAsyncThunk(
  "customerProfile/resetExpensesAndLiabilitiesShares",
  async (data, { rejectWithValue }) => {
    const customer = await reusableFetch(
      `customer/reset-expenses-and-liabilities-shares`,
      "PUT",
      data,
      rejectWithValue,
    );
    return customer;
  },
);

export const updateNotes = createAsyncThunk("notes/update", async (data, { rejectWithValue }) => {
  const notes = await reusableFetch(`notes`, "POST", data, rejectWithValue);
  return notes;
});

export const updateExpenses = createAsyncThunk("expenses/update", async (data, { rejectWithValue }) => {
  const expenses = await reusableFetch(`expenses`, "PUT", data, rejectWithValue);
  return expenses;
});

export const createFinanceItem = createAsyncThunk(
  "finance-item/create",
  async ({ data, endpoint }, { rejectWithValue }) => {
    const response = await reusableFetch(endpoint, "POST", data, rejectWithValue);
    return response;
  },
);

export const deleteFinanceItem = createAsyncThunk(
  "finance-item/delete",
  async ({ data, endpoint }, { rejectWithValue }) => {
    const response = await reusableFetch(endpoint, "DELETE", data, rejectWithValue);
    return response;
  },
);

export const uploadDocument = createAsyncThunk("document/upload", async ({ formData }, { rejectWithValue }) => {
  const token = sessionStorage.token;
  const csrfToken = sessionStorage.getItem("csrfToken");
  try {
    if (formData) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/document/upload`, {
        method: "POST",
        headers: {
          ...(token ? { "x-access-token": token } : {}),
          ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
        },
        body: formData,
        credentials: "include",
        signal: AbortSignal.timeout(30000),
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return { data, status: response.status };
    }
  } catch (e) {
    return rejectWithValue(e.message || e);
  }
});

export const uploadEntityDocument = createAsyncThunk(
  "entity/document/upload",
  async ({ formData }, { rejectWithValue }) => {
    const entityDocument = await reusableFormDataFetch(
      `entity/document/upload`,
      "POST",
      formData,
      rejectWithValue,
      false,
    );
    return entityDocument;
  },
);

export const updateDeclaration = createAsyncThunk("declaration/update", async (data, { rejectWithValue }) => {
  const declaration = await reusableFetch(`declaration`, "POST", data, rejectWithValue);
  return declaration;
});

export const updateSupplier = createAsyncThunk("supplier/update", async (data, { rejectWithValue }) => {
  const supplier = await reusableFetch(`supplier`, "POST", data, rejectWithValue);
  return supplier;
});

export const updateCreditProfile = createAsyncThunk("credit-profile/update", async (data, { rejectWithValue }) => {
  const file = await reusableFetch(`credit-profile`, "PUT", data, rejectWithValue);
  return file;
});

export const updateFinanceItem = createAsyncThunk("financeItem/update", async (data, { rejectWithValue, getState }) => {
  const financeItem = await reusableFetch(`finance-item`, "PUT", data, rejectWithValue);
  return financeItem;
});

export const submitPrivacy = createAsyncThunk("document/privacy", async (data, { rejectWithValue, getState }) => {
  const { jwt, ...rest } = data;
  const privacy = await reusableFetch(`document/privacy`, "POST", rest, rejectWithValue, false, jwt);
  return privacy;
});

export const submitDisclosure = createAsyncThunk("document/disclosure ", async (data, { rejectWithValue }) => {
  const { jwt, ...rest } = data;
  const disclosure = await reusableFetch(`document/disclosure`, "POST", rest, rejectWithValue, false, jwt);
  return disclosure;
});

export const addActivityLog = createAsyncThunk("document/activity-log ", async (data, { rejectWithValue }) => {
  const { jwt, ...rest } = data;
  const addedActivity = await reusableFetch(`document/activity-log`, "POST", rest, rejectWithValue, false, jwt);
  return addedActivity;
});

export const createLiabilities = createAsyncThunk(
  "userDetails/createLiabilities",
  async ({ data }, { rejectWithValue }) => {
    const liabilitiesList = await reusableFetch(`liabilities`, "POST", data, rejectWithValue);
    return liabilitiesList;
  },
);

export const deleteLiabilities = createAsyncThunk(
  "userDetails/deleteLiabilities",
  async ({ data }, { rejectWithValue }) => {
    const liabilitiesList = await reusableFetch(`liabilities`, "DELETE", data, rejectWithValue);
    return liabilitiesList;
  },
);

export const updateLiabilities = createAsyncThunk(
  "userDetails/updateLiabilities",
  async ({ data }, { rejectWithValue }) => {
    const liabilitiesList = await reusableFetch(`liabilities`, "PUT", data, rejectWithValue);
    return liabilitiesList;
  },
);

export const createIncome = createAsyncThunk("userDetails/createIncome", async ({ data }, { rejectWithValue }) => {
  const incomeList = await reusableFetch(`income`, "POST", data, rejectWithValue);
  return incomeList;
});

export const updateIncome = createAsyncThunk("userDetails/updateIncome", async ({ data }, { rejectWithValue }) => {
  const incomeList = await reusableFetch(`income`, "PUT", data, rejectWithValue);
  return incomeList;
});

export const deleteIncome = createAsyncThunk("userDetails/deleteIncome", async ({ data }, { rejectWithValue }) => {
  const incomeList = await reusableFetch(`income`, "DELETE", data, rejectWithValue);
  return incomeList;
});

export const createExpense = createAsyncThunk("userDetails/createExpense", async ({ data }, { rejectWithValue }) => {
  const expensesList = await reusableFetch(`expenses`, "POST", data, rejectWithValue);
  return expensesList;
});

export const updateExpense = createAsyncThunk("userDetails/updateExpense", async ({ data }, { rejectWithValue }) => {
  const expensesList = await reusableFetch(`expenses`, "PUT", data, rejectWithValue);
  return expensesList;
});

export const deleteExpense = createAsyncThunk("userDetails/deleteExpense", async ({ data }, { rejectWithValue }) => {
  const expensesList = await reusableFetch(`expenses`, "DELETE", data, rejectWithValue);
  return expensesList;
});

export const deleteEmployer = createAsyncThunk("userDetails/deleteEmployer", async ({ data }, { rejectWithValue }) => {
  const employersList = await reusableFetch(`employer`, "DELETE", data, rejectWithValue);
  return employersList;
});

export const createAssets = createAsyncThunk("userDetails/createAssets", async ({ data }, { rejectWithValue }) => {
  const assetsList = await reusableFetch(`customer-assets`, "POST", data, rejectWithValue);
  return assetsList;
});

export const updateAssets = createAsyncThunk("userDetails/updateAssets", async ({ data }, { rejectWithValue }) => {
  const assetsList = await reusableFetch(`customer-assets`, "PUT", data, rejectWithValue);
  return assetsList?.data;
});

export const deleteAssets = createAsyncThunk("userDetails/deleteAssets", async ({ data }, { rejectWithValue }) => {
  const assetsList = await reusableFetch(`customer-assets`, "DELETE", data, rejectWithValue);
  return assetsList;
});

/**
 * Which vehicle-data provider serves the asset lookup cascade for this tenant ("GLASSES" | "AUTOGRAB").
 * The lookup endpoints themselves are provider-agnostic — this only drives labelling.
 */
export const getAssetLookupProvider = createAsyncThunk(
  "asset/getAssetLookupProvider",
  async (_arg, { rejectWithValue }) => {
    const response = await reusableFetch(`asset/lookupProvider`, "GET", null, rejectWithValue);
    return response;
  },
  {
    // Tenant-level and stable for the session, but every asset card mounts this — resolve it once.
    // Every card mounts in the same tick, so `resolved` (set on settle) is still false for all of
    // them; the in-flight flag is what collapses those simultaneous mounts into a single request.
    condition: (_arg, { getState }) => {
      const { assetLookupProviderResolved, assetLookupProviderLoading } = getState().userDetails;
      return !assetLookupProviderResolved && !assetLookupProviderLoading;
    },
  },
);

export const getListOfMakes = createAsyncThunk("asset/getListOfMakes", async (data, { rejectWithValue }) => {
  const assetsList = await reusableFetch(
    `asset/getCombinedListOfMakes?ModelTypeCode=${data.assetTypeCode}&YearCreate=${data.year}&assetId=${data.assetId}`,
    "GET",
    null,
    rejectWithValue,
  );
  return assetsList;
});

export const getListOfMakesYearFromTo = createAsyncThunk(
  "asset/getListOfMakesYearFromTo",
  async (data, { rejectWithValue }) => {
    const assetsList = await reusableFetch(
      `asset/getCombinedListOfMakesYearFromTo?ModelTypeCode=${data.assetTypeCode}&YearFrom=${data.from}&YearTo=${data.to}&assetId=${data.assetId}`,
      "GET",
      null,
      rejectWithValue,
    );
    return assetsList;
  },
);

export const getListOfModels = createAsyncThunk("asset/getListOfModels", async (data, { rejectWithValue }) => {
  const assetsList = await reusableFetch(
    `asset/getCombinedListOfModels?ModelTypeCode=${data.assetTypeCode}&YearCreate=${data.year}&ManufacturerCode=${data.makeCode}&assetId=${data.assetId}`,
    "GET",
    null,
    rejectWithValue,
  );
  return assetsList;
});

export const getListOfModelsYearFromTo = createAsyncThunk(
  "asset/getListOfModelsYearFromTo",
  async (data, { rejectWithValue }) => {
    const assetsList = await reusableFetch(
      `asset/getCombinedListOfModelsYearFromTo?ModelTypeCode=${data.assetTypeCode}&YearFrom=${data.from}&YearTo=${data.to}&ManufacturerCode=${data.makeCode}&assetId=${data.assetId}`,
      "GET",
      null,
      rejectWithValue,
    );
    return assetsList;
  },
);

export const getListOfVariants = createAsyncThunk("asset/getListOfVariants", async (data, { rejectWithValue }) => {
  const assetsList = await reusableFetch(
    // `asset/getListOfVariants?ModelTypeCode=${data.assetTypeCode}&YearCreate=${data.year}&ManufacturerCode=${data.makeCode}&FamilyCode=${data.modelCode}`,
    `asset/getCombinedListOfVariants?ModelTypeCode=${data.assetTypeCode}&YearCreate=${data.year}&ManufacturerCode=${data.makeCode}&FamilyCode=${data.modelCode}&assetId=${data.assetId}`,
    "GET",
    null,
    rejectWithValue,
  );
  return assetsList;
});

export const getListOfVariantsYearFromTo = createAsyncThunk(
  "asset/getListOfVariantsYearFromTo",
  async (data, { rejectWithValue }) => {
    const assetsList = await reusableFetch(
      `asset/getListOfVariantsYearFromTo?ModelTypeCode=${data.assetTypeCode}&YearFrom=${data.from}&YearTo=${data.to}&ManufacturerCode=${data.makeCode}&FamilyCode=${data.modelCode}&assetId=${data.assetId}`,
      "GET",
      null,
      rejectWithValue,
    );
    return assetsList;
  },
);

export const getListOfNVICForAdvancedSearch = createAsyncThunk(
  "asset/getListOfNVICForAdvancedSearch",
  async (data, { rejectWithValue }) => {
    const assetsList = await reusableFetch(
      // `asset/GetListOfNVICForAdvancedSearch?ModelTypeCode=APAS&YearCreate=2019&ManufacturerCode=ALF&FamilyCode=GIU&VariantName=QUADRIFOGLIO&SeriesCode=19&Keyword=A`,
      `asset/getListOfNVICForAdvancedSearch?ModelTypeCode=${data.assetTypeCode}&YearCreate=${data.yearCreate}&ManufacturerCode=${data.makeCode}&FamilyCode=${data.modelCode}&VariantName=${data.variant}&Keyword=A&assetId=${data.assetId}`,
      "GET",
      null,
      rejectWithValue,
    );
    return assetsList;
  },
);

/**
 * Free-text vehicle search. Hits the same picker endpoint as the make/model cascade, but with only a
 * keyword: AutoGrab answers an unconstrained request from its ranked `/vehicles/?search=` catalogue.
 * Results are kept in their own list so a search never clobbers the cascade's `NVICList`.
 */
export const searchVehiclesByKeyword = createAsyncThunk(
  "asset/searchVehiclesByKeyword",
  async (data, { rejectWithValue }) => {
    const vehicles = await reusableFetch(
      `asset/getListOfNVICForAdvancedSearch?ModelTypeCode=${data.assetTypeCode}&Keyword=${encodeURIComponent(data.keyword)}&assetId=${data.assetId}`,
      "GET",
      null,
      rejectWithValue,
    );
    return vehicles;
  },
);

export const getDetailsSpecificationAll = createAsyncThunk(
  "asset/getDetailsSpecificationAll",
  async (data, { rejectWithValue }) => {
    // Glasses only: an AutoGrab pick resolves through `getVehicleSpecification` instead.
    const modelTypeCode = encodeURIComponent(data.assetTypeCode || "A");
    const modelSpecificationCode = encodeURIComponent(data.modelSpecificationCode || "A");
    const nvic = encodeURIComponent(data.NVIC || "");
    const assetId = encodeURIComponent(data.assetId || "");
    const assetsList = await reusableFetch(
      `asset/getDetailsSpecificationAll?ModelTypeCode=${modelTypeCode}&ModelSpecificationCode=${modelSpecificationCode}&NVIC=${nvic}&assetId=${assetId}`,
      "GET",
      null,
      rejectWithValue,
    );
    return assetsList;
  },
);

export const getVehicleSpecification = createAsyncThunk(
  "asset/getVehicleSpecification",
  async (data, { rejectWithValue }) => {
    const query = new URLSearchParams({
      vehicleId: data.vehicleId || "",
      assetId: data.assetId || "",
      applicationId: data.applicationId || "",
    });
    const specification = await reusableFetch(
      `asset/getAutoDetailsSpecification?${query.toString()}`,
      "GET",
      null,
      rejectWithValue,
    );
    return specification;
  },
);

export const getVehicleLookup = createAsyncThunk("asset/getVehicleLookup", async (query, { rejectWithValue }) => {
  const assetsList = await reusableFetch(
    `asset/getVehicleLookup?${query}&include_nvic=true`,
    "GET",
    null,
    rejectWithValue,
  );
  return assetsList;
});

export const getVehiclePPSRSearch = createAsyncThunk(
  "asset/getVehiclePPSRSearch",
  async (query, { rejectWithValue }) => {
    const result = await reusableFetch(`asset/getVehiclePPSRSearch?${query}`, "GET", null, rejectWithValue);
    return result;
  },
);

export const updateApplicationAgreement = createAsyncThunk(
  "userDetails/updateApplicationAgreement",
  async (detail, { rejectWithValue }) => {
    const data = {
      introducerDeclarationAgree: detail.introducerDeclarationAgree,
    };
    const assetsList = await reusableFetch(
      `application/introducerDeclaration/${detail._id}`,
      "POST",
      data,
      rejectWithValue,
    );
    return assetsList;
  },
);

export const getApplicationDocumentPreview = createAsyncThunk(
  "application/getApplicationDocumentPreview",
  async (data, { rejectWithValue }) => {
    const docTemplate = await reusableFetch(
      `template/application/document/preview/${data.templateType}`,
      "POST",
      data,
      rejectWithValue,
      false,
    );
    return docTemplate;
  },
);

export const sendDocumentSignInLink = createAsyncThunk(
  "application/document/signIn/link",
  async (data, { rejectWithValue }) => {
    const disclosure = await reusableFetch(`document/signIn/link`, "POST", data, rejectWithValue);
    return disclosure;
  },
);

export const updateNccpDeclaration = createAsyncThunk(
  "application/updateNccpDeclaration",
  async (data, { rejectWithValue }) => {
    const application = await reusableFetch(`customer-disclaimer`, "PUT", data, rejectWithValue);
    return application;
  },
);

export const updateAcknowledgement = createAsyncThunk(
  "application/updateAcknowledgement",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch("acknowledgement", "PUT", data, rejectWithValue);
    return response;
  },
);

export const customerLogin = createAsyncThunk("customer/login", async (data, { rejectWithValue }) => {
  const response = await reusableWithoutTokenFetch(`auth/customer/login`, "POST", data, rejectWithValue);
  return response;
});

export const sendCustomerDetailsLinkToCustomer = createAsyncThunk(
  "customer/sendLink",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch(`customer/details/link`, "POST", data, rejectWithValue);
    return response;
  },
);

const DOCUMENT_REQUEST_ENTITY_TYPES = new Set(["customer", "asset"]);

const getDocumentRequestEntityType = (entityType = "customer") =>
  DOCUMENT_REQUEST_ENTITY_TYPES.has(entityType) ? entityType : "customer";

const getDocumentRequestPath = (entityType, path) => `${getDocumentRequestEntityType(entityType)}/documents/${path}`;

export const sendDocumentRequestLinkToCustomer = createAsyncThunk(
  "customer/sendDocumentRequestLink",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch(
      getDocumentRequestPath("customer", "request/link"),
      "POST",
      data,
      rejectWithValue,
    );
    return response;
  },
);

export const sendDocumentRequestLinkToAsset = createAsyncThunk(
  "asset/sendDocumentRequestLink",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch(
      getDocumentRequestPath("asset", "request/link"),
      "POST",
      data,
      rejectWithValue,
    );
    return response;
  },
);

export const getDocumentRequestProgress = createAsyncThunk(
  "customer/getDocumentRequestProgress",
  async ({ token, entityType = "customer", jwt }, { rejectWithValue }) => {
    const response = await reusableFetch(
      getDocumentRequestPath(entityType, `request/${token}`),
      "GET",
      null,
      rejectWithValue,
      false,
      jwt,
    );
    return response;
  },
);

export const submitDocuments = createAsyncThunk("customer/submitDocuments", async (data = {}, { rejectWithValue }) => {
  const { entityType = "customer", jwt, ...payload } = data;
  const response = await reusableFetch(
    getDocumentRequestPath(entityType, "submit"),
    "POST",
    payload,
    rejectWithValue,
    false,
    jwt,
  );
  return response;
});

export const saveCustomerDetails = createAsyncThunk("customer/save", async (data, { rejectWithValue }) => {
  const response = await reusableFetch(
    `customer/details/save`,
    "POST",
    data,
    rejectWithValue,
    false,
    sessionStorage.getItem("token") || undefined,
  );
  return response;
});

export const getCloneApplication = createAsyncThunk("application/clone", async (applicationId, { rejectWithValue }) => {
  const response = await reusableFetch(`application/clone/${applicationId}`, "POST", null, rejectWithValue);
  // reusableFetch already returns the rejectWithValue wrapper on failure - returning it as-is
  // keeps the API error body as the rejected action payload instead of nesting it one level deeper.
  if (!isFetchResult(response)) {
    return response;
  }
  return response.data;
});

export const confirmCustomerData = createAsyncThunk(
  "customer/confirmCustomerData",
  async (data, { rejectWithValue }) => {
    const customer = await reusableFetch(`customer/`, "PUT", data, rejectWithValue);
    return customer;
  },
);

export const getApplicationDocuments = createAsyncThunk(
  "application/documents",
  async ({ applicationId }, { rejectWithValue }) => {
    const res = await reusableFetch(`application/documents/${applicationId}`, "GET", null, rejectWithValue);
    return res;
  },
);

export const uploadAssetInvoice = createAsyncThunk(
  "application/uploadAssetInvoice",
  async ({ formData, jwt }, { rejectWithValue }) => {
    const assetInvoice = await reusableFormDataFetch(
      `document/asset/invoice`,
      "POST",
      formData,
      rejectWithValue,
      true,
      jwt,
    );
    return assetInvoice;
  },
);

export const requestAssetInvoice = createAsyncThunk(
  "application/requestAssetInvoice",
  async (data, { rejectWithValue }) => {
    const assetInvoiceRequest = await reusableFetch(`asset/documents/request/link`, "POST", data, rejectWithValue);
    return assetInvoiceRequest;
  },
);

export const removeAssetInvoice = createAsyncThunk(
  "application/removeAssetInvoice",
  async ({ documentId, assetId, applicationId, jwt }, { rejectWithValue }) => {
    const assetInvoice = await reusableFetch(
      `document/asset/invoice/${documentId}`,
      "DELETE",
      { assetId, applicationId },
      rejectWithValue,
      false,
      jwt,
    );
    return assetInvoice;
  },
);

export const createInsuranceQuote = createAsyncThunk(
  "application/createInsuranceQuote",
  async (data, { rejectWithValue }) => {
    const insuranceQuote = await reusableFetch(`asset/insuranceQuote`, "POST", data, rejectWithValue);
    return insuranceQuote;
  },
);

export const sendInsuranceQuote = createAsyncThunk(
  "application/sendInsuranceQuote",
  async (data, { rejectWithValue }) => {
    const insuranceQuoteLink = await reusableFetch(
      `asset/send/insuranceQuote/${data.quoteGuid}?assetId=${data.assetId}&applicationId=${data.applicationId}&to=${data.to}`,
      "GET",
      null,
      rejectWithValue,
    );
    return insuranceQuoteLink;
  },
);

export const createProductQuote = createAsyncThunk(
  "application/createProductQuote",
  async (data, { rejectWithValue }) => {
    const productsQuote = await reusableFetch(`products-quote`, "POST", data, rejectWithValue);
    return productsQuote;
  },
);

export const removeProductQuote = createAsyncThunk(
  "application/removeProductQuote",
  async ({ productQuoteId, applicationId }, { rejectWithValue }) => {
    const productsQuote = await reusableFetch(
      `products-quote/${productQuoteId}`,
      "DELETE",
      { applicationId },
      rejectWithValue,
    );
    return productsQuote;
  },
);

export const updateProductQuote = createAsyncThunk(
  "application/updateProductQuote",
  async ({ productQuoteId, ...data }, { rejectWithValue }) => {
    const productsQuote = await reusableFetch(`products-quote/${productQuoteId}`, "PATCH", data, rejectWithValue);
    return productsQuote;
  },
);

export const sendProductQuotesToCustomers = createAsyncThunk(
  "application/sendProductQuotesToCustomers",
  async (data, { rejectWithValue }) => {
    const productsQuote = await reusableFetch(`products-quote/send-to-customers`, "POST", data, rejectWithValue);
    return productsQuote;
  },
);

export const setPrimaryProductQuote = createAsyncThunk(
  "application/setPrimaryProductQuote",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch("products-quote/makeQuotePrimary", "PUT", data, rejectWithValue);
    return response;
  },
);

export const transferApplicationOwnership = createAsyncThunk(
  "application/transferApplicationOwnership",
  async (data, { rejectWithValue }) => {
    const application = await reusableFetch(`application/transfer-ownership`, "POST", data, rejectWithValue);
    return application;
  },
);

export const updateApplicationFollowers = createAsyncThunk(
  "application/updateApplicationFollowers",
  async (data, { rejectWithValue }) => {
    const application = await reusableFetch(`application/followers`, "PUT", data, rejectWithValue);
    return application;
  },
);

/** These lookups are dispatched as a query string, so the asset id is read back out of it. */
const getAssetTargetFromQuery = (state, action) =>
  getAssetTarget(state, null, action.meta.arg?.includes("assetId") ? action.meta.arg.split("assetId=")[1] : undefined);

/**
 * A personal finance delete answers with the customer's remaining items, which is empty once the last one is
 * gone, so the customer is found from the request rather than from the first item of the response.
 * A response without a list says nothing about what is left, so the customer's items are kept as they are.
 */
const setCustomerListAfterDelete = (state, { payload, meta }, listKey) => {
  const remaining = payload?.data?.data;
  const customer = state.application.customers?.find((el) => el?._id === meta.arg?.data?.customer);
  if (customer && Array.isArray(remaining)) {
    customer[listKey] = remaining;
  }
};

const initialState = {
  /** Tenant-level vehicle-data provider for the asset lookup cascade. */
  assetLookupProvider: "GLASSES",
  assetLookupProviderResolved: false,
  assetLookupProviderLoading: false,
  assetLookupProviderSettled: false,
  demoLoanDetailsFields: {},
  singleEntityElement: {},
  gettingQuote: null,
  gettingInsuranceQuote: {},
  sendingInsuranceQuote: {
    loading: false,
    to: "", // keyof INSURANCE_QUOTE_TO
  },
  confirmCustomerDataLoader: false,
  isIntercomChatOptions: {
    showIntercom: false,
    intercomData: null,
    service: "",
  },
  assetDocumentUploadLoading: false,
  requestAssetInvoiceLoading: false,
  submittingSignature: false,
  gettingClonedApplication: false,
  gettingAdditionalAsset: false,
  removeAssetModalOptions: {
    isOpen: false,
    assetType: "",
    assetId: "",
    isRemovingAsset: false,
  },
  cloneAssetLoaderOptions: {
    assetId: "",
    isLoading: false,
  },
  makeAssetPrimaryLoaderOptions: {
    assetId: "",
    isLoading: false,
  },
  startApplicationBtnLoader: false,
  isNewApplication: undefined,
  gettingCalculator: false,
  loadingSubmitApplication: false,
  submittingApplicationToEmail: false,
  verifiedDialogBoxIsOpen: false,
  customerPrivacyRequest: false,
  idVerificationRequest: false,
  bankStatementsRequest: false,
  bankStatementsLoading: false,
  isApplicationMenuOpen: true,
  isLoanSummaryOpen: false,
  isCommentSectionOpen: false,
  linkSent: false,
  sendingIDLink: null,
  ApplicationBtn: false,
  gettingASICData: null,
  gettingASICPPSRData: null,
  gotASICData: null,
  gettingEntityDetails: false,
  gettingCreditProfile: false,
  gettingEquifaxCreditFile: false,
  gettingEquifaxCreditCompanyFile: false,
  isDeletingDocument: false,
  canCalculateQuote: false,
  gettingAddressDetails: false,
  isManualAddress: {
    show: false,
    customerId: null,
  },
  currentProductLenderId: "",
  directors: [],
  shareHolders: [],
  beneficiaries: [],
  shares: [],
  businessAddresses: [],
  applicants: [],
  entityId: "",
  entity: {},
  identityVerification: [],
  identityDocument: [],
  address: {},
  customer: {},
  document: {},
  creditProfile: {},
  open: false,
  uploadPrivacyLoading: "",
  uploadIDLoading: "",
  documentUploadLoding: "",
  entityDocumentUploadLoadingId: "",
  isUpdatedLoanDetails: null,
  gettingApplication: false,
  trasferingApplicationOwnership: false,
  updatingApplicationStatus: false,
  updatingApplicationType: false,
  downloadApplicationLoader: false,
  createProductQuoteLoader: false,
  updateProductQuoteLoader: false,
  removeProductQuoteLoader: false,
  productQuoteNoteModal: {
    open: false,
    mode: "save",
    productQuoteId: null,
    initialNotes: "",
  },
  setPrimaryProductQuoteLoaderOptions: {
    productQuoteId: null,
    isLoading: false,
  },
  sendProductQuotesToCustomersLoader: false,
  disclosureLoader: false,
  documentLinkSentLoader: false,
  documentPreview: null,
  showPreviewDocument: {
    isOpen: false,
    customerId: "",
    templateType: "",
    disclosureInfo: null,
  },
  requestDocumentsModal: {
    isOpen: false,
    customerId: "",
  },
  activityLog: [],
  applicationServiceLog: [],
  creditScoreTypes: [],
  applicationServiceLogLoader: false,
  openManualQuote: false,
  openProductsQuotes: false,
  removeEmployerLoader: true,
  openAssignOwnershipModal: false,
  isOpenFollowersModal: false,
  updatingApplicationFollowers: false,
  isConfirmingShareType: false,
  updatingCreditScoreModeCusId: null,
  personalFinLoaders: {
    id: "",
    addAssetLoader: false,
    removeAssetLoader: false,
    addLiabilityLoader: false,
    removeLiabilityLoader: false,
    addIncomeLoader: false,
    removeIncomeLoader: false,
    addExpenseLoader: false,
    removeExpenseLoader: false,
  },
  // Keyed by asset id so each asset keeps its own expanded sections.
  assetExpandedSections: {},
  // Keyed by entity id, then check, so each CreditorWatch check keeps its own loader.
  gettingCommercialCreditChecks: {},
  // Keyed by customer id, so each customer's Equifax employment and income verification keeps its own loader.
  requestingEmploymentIncomeVerification: {},
  removingEntityOptions: {
    isLoading: false,
    entityId: "",
  },
  requireFieldErrors: {},
  validationRequireFieldError: {},
  lenderRequiredFieldsError: {
    assetsFieldErrors: {
      assetValue: "",
      typeOfSale: "",
      condition: "",
      make: "",
      model: "",
      glassesMake: "",
      glassesModel: "",
      glassesVariant: "",
      glassesNVIC: "",
      glassesData: "",
    },
    loanDetailsFieldErrors: {},
    consumerFieldErrors: {
      customerTitle: "",
      firstName: "",
      lastName: "",
      IDNumber: "",
      dateOfBirth: "",
      IDCardNumber: "",
      phoneNumber: "",
      emailAddress: "",
      address: "",
      yearsAt: "",
      monthsAt: "",
      IDExpiryDate: "",
      customerAssets: {
        assetType: "",
        value: "",
        description: "",
      },
      liabilities: {
        liabilitiesType: "",
        outstandingAmount: "",
        limit: "",
        financier: "",
        repaymentFrequency: "",
        repaymentAmount: "",
        description: "",
        shareOfLiability: 100,
      },
      income: {
        incomeType: "",
        amount: "",
        frequency: "",
      },
      expenses: {
        expensesType: "",
        amount: "",
        frequency: "",
        shareOfExpense: 100,
      },
    },
    agreementsFieldErrors: {
      introducerDeclarationAgree: "",
    },
    nccpFieldErrors: {
      whatIsThePurposeOfThisApplication: "", // Enum
      whyDoesThisLoanTermSuitYourRequirements: "", // Enum
      whatAreYouLookingForInYourLoan: "", // Enum
      anyForeseeableChangesImpactingLoanRepayment: "", // Enum
      howApplicantIntendToDoLoanRepaymentAfterChange: "", // Textarea
      whyIsThisLoanNotUnsuitableForTheApplicantDetail: "", // Textarea
      increaseInLoanAmountForFinancingCostOfInsurance: "", // boolean
      balloonPaymentAtTheEndOfTheLoan: "", // boolean
      whatToDoWithBalloonPaymentAtTheEndOfTheLoanTerm: "", // Enum
    },
    entityFieldErrors: {
      address: "",
    },
    guarantorFieldErrors: {
      customerTitle: "",
      firstName: "",
      middleNames: "",
      lastName: "",
      dateOfBirth: "",
      IDNumber: "",
      IDCardNumber: "",
      Number: "",
      Address: "",
      address: "",
      yearsAt: "",
      monthsAt: "",
      IDExpiryDate: "",
    },
  },
  application: {
    asset: {
      nvicLoaderData: false,
      // Initial state: false so pickers don't wait on unmade requests.
      makeLoader: false,
      modelLoader: false,
      variantLoader: false,
      NVICLoader: false,
      searchVehicleLoader: false,
      searchPPSRLoader: false,
      blueflagPPSR: {
        search: [],
        nevdisData: {},
      },
      documents: [],
    },
    additionalAssets: [],
    loanDetails: {},
    followers: [],
    submissionModal: {
      steps: [],
      isOpen: false,
      lender: undefined,
      acknowledgement: undefined,
      lodgeAI: undefined,
      submission: {
        status: undefined,
      },
    },
  },
  // =============================    Calculator Errors   =================================
  calculatorFieldsErrors: {
    finance: {
      lender: "",
      state: "",
      itcLevel: "",
    },
    costOfGoods: {
      net: "",
      gst: "",
      costOfGoodTotal: "",
      additionalFees: "",
      additionalTaxes: "",
    },
    deposit: {
      cash: "",
      tradeIn: "",
    },
    revenue: {
      financedFees: "",
      financedBrokerage: "",
      unFinancedFees: "",
      unFinancedBrokerage: "",
      accountFee: "",
    },
    brokerage: {
      brokerage: "",
      // brokerageAmount: ""
    },
    amountFinanced: {
      brokerage: "",
    },
    balloon: {
      balloon: "",
      // balloonAmount: ""
    },
    rates: {
      rate: "",
      effectiveRate: "",
      gst: "",
    },
    repaymentSchedule: {
      repayment: "",
      repaymentFrequency: "",
      term: "",
      structure: "",
      amortDate: "",
    },
    singleRepayment: {
      repayments: "",
      gst: "",
      accountFee: "",
    },
    allRepayment: {
      net: "",
      gst: "",
      taxExc: "",
      taxInc: "",
    },
    termsCharges: {
      termCharges: "",
    },
  },
};

const setCommercialCreditCheckLoading = (state, { entityId, check }, isLoading) => {
  state.gettingCommercialCreditChecks[entityId] = {
    ...state.gettingCommercialCreditChecks[entityId],
    [check]: isLoading,
  };
};

export const formdataSlice = createSlice({
  name: "userDetails",
  initialState,
  reducers: {
    clearState: () => initialState,
    clearUpFinances: (state) => {
      state.assetListData = initialState.assetListData;
      state.liabilitiesListData = initialState.liabilitiesListData;
      state.incomeListData = initialState.incomeListData;
      state.expensesListData = initialState.expensesListData;
    },
    saveValue: (state, action) => Object.assign({}, state, action.payload),
    setAssetExpandedSection: (state, { payload }) => {
      const { assetId, section, isExpanded } = payload;
      const expanded = state.assetExpandedSections[assetId] ?? DEFAULT_EXPANDED_ASSET_SECTIONS;

      state.assetExpandedSections[assetId] = isExpanded
        ? [...new Set([...expanded, section])]
        : expanded.filter((item) => item !== section);
    },
    getCurrentProductLenderId: (state, { payload }) => {
      state.currentProductLenderId = payload;
    },
    openProductQuoteNoteModalForSave: (state) => {
      state.productQuoteNoteModal = {
        open: true,
        mode: "save",
        productQuoteId: null,
        initialNotes: "",
      };
    },
    openProductQuoteNoteModalForUpdate: (state, { payload }) => {
      state.productQuoteNoteModal = {
        open: true,
        mode: "update",
        productQuoteId: payload?.productQuoteId ?? null,
        initialNotes: payload?.initialNotes ?? "",
      };
    },
    closeProductQuoteNoteModal: (state) => {
      state.productQuoteNoteModal = {
        open: false,
        mode: "save",
        productQuoteId: null,
        initialNotes: "",
      };
    },
    clearProductsQuotes: (state) => {
      state.application.productsQuotes = [];
    },
    singleEntity: (state, { payload }) => {
      state.singleEntityElement = payload;
    },

    updateLiabilitiesList: (state, { payload }) => {
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === payload[0].customer);
      state.application.customers[findindex].liabilities = payload;
    },
    updateIncomeList: (state, { payload }) => {
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === payload[0].customer);
      state.application.customers[findindex].income = payload;
    },
    updateExpensesList: (state, { payload }) => {
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === payload[0].customer);

      state.application.customers[findindex].expenses = payload;
    },
    updateAssetList: (state, { payload }) => {
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === payload[0].customer);

      state.application.customers[findindex].customerAssets = payload;
    },
    saveAsset: (state, action) => {
      state.application.asset = merge(state.application.asset, action.payload);
    },
    saveLoanDetails: (state, action) => {
      state.application.loanDetails = merge(state.application.loanDetails, action.payload);
    },
    verifiedDialogBox: (state, { payload }) => {
      state.verifiedDialogBoxIsOpen = payload;
    },
    updateAgeOfAsset: (state, { payload }) => {
      const stateData = current(state.application);
      const isMainAsset = stateData.asset._id === payload?._id;
      if (isMainAsset) {
        state.application.asset.ageOfAsset = payload;
      } else {
        let findindex = stateData.additionalAssets.findIndex((el) => el?._id === payload?._id);
        if (findindex !== -1) {
          state.application.additionalAssets[findindex].ageOfAsset = payload;
        }
      }
    },
    updateGlassAsset: (state, { payload }) => {
      const stateData = current(state.application);
      const isMainAsset = stateData.asset._id === payload?._id;
      if (isMainAsset) {
        state.application.asset = payload;
      } else {
        let findindex = stateData.additionalAssets.findIndex((el) => el?._id === payload?._id);
        if (findindex !== -1) {
          state.application.additionalAssets[findindex] = payload;
        }
      }
    },
    saveCustomer: (state, action) => {
      merge(state.application.customers[0], action.payload);
    },
    setRequiredFieldsError: (state, { payload }) => {
      state.lenderRequiredFieldsError = payload;
    },
    setRequiredFieldsErrors: (state, { payload }) => {
      state.requireFieldErrors = payload;
    },
    setValidationRequireFieldError: (state, { payload }) => {
      state.validationRequireFieldError = payload;
    },
    setCalculatorFieldsError: (state, { payload }) => {
      state.calculatorFieldsErrors = payload;
    },
    setNewCommercialCreditFile: (state, { payload }) => {
      const foundEntity = state.application.entities.find((entity) => entity?._id === payload?.entity);
      if (foundEntity) {
        foundEntity.documents.push(payload);
      }
    },
    setNewCustomerCreditFile: (state, { payload }) => {
      const foundCustomer = state.application.customers.find((customer) => customer?._id === payload?.customer);
      if (foundCustomer) {
        foundCustomer.documents.push(payload);
      }
    },
    setNewCustomerBankStatementFile: (state, { payload }) => {
      const foundCustomer = state.application.customers.find((customer) => customer?._id === payload?.customer);
      if (foundCustomer) {
        foundCustomer.documents.push(payload);
      }
    },
    setNewDocumentFile: (state, { payload }) => {
      const foundCustomer = state.application.customers.find((customer) => customer?._id === payload?.customer);
      if (foundCustomer) {
        foundCustomer.documents.push(payload);
      }
    },
    setNewAssetPPSRFile: (state, { payload }) => {
      const stateData = current(state.application);
      const assetId = payload.assetId;
      const isMainAsset = stateData.asset._id === assetId;
      if (isMainAsset) {
        state.application.asset.documents.push(payload.certificateDocument);
      } else {
        let findindex = stateData.additionalAssets.findIndex((el) => el?._id === assetId);
        if (findindex !== -1) {
          state.application.additionalAssets[findindex].documents.push(payload.certificateDocument);
        }
      }
    },
    setNewActivityLog: (state, { payload }) => {
      state.activityLog.push(payload);
    },
    setNewServiceUsageLog: (state, { payload }) => {
      state.applicationServiceLog.unshift(payload);
    },
    updateCustomerData: (state, { payload }) => {
      const customerId = payload?.customerId || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === customerId);
      state.application.customers[findindex][payload?.key] = payload?.value;
    },
    setSubmissionModal: (state, { payload }) => {
      const currentSubmissionModal = state.application.submissionModal;
      const updatedSubmissionModal = {
        ...currentSubmissionModal,
        ...payload,
      };
      state.application.submissionModal = updatedSubmissionModal;
    },
    setSubmissionModalOpen: (state, { payload }) => {
      const { isOpen } = payload;
      state.application.submissionModal.isOpen = isOpen;
    },
    setSubmissionStepCompleted: (state, { payload }) => {
      const { type } = payload;
      const currentSubmissionSteps = state.application.submissionModal.steps;
      const updatedSubmissionSteps = currentSubmissionSteps.map((step) =>
        step.type === type ? { ...step, isCompleted: true } : step,
      );
      state.application.submissionModal.steps = updatedSubmissionSteps;
    },
    setSubmissionSelectedDocuments: (state, { payload }) => {
      const { selectedDocuments } = payload;
      state.application.submissionModal.selectedDocuments = selectedDocuments;
    },
    setIntercomChatOptions: (state, { payload }) => {
      state.isIntercomChatOptions = payload;
    },
    // NOTE: For some reason the disclaimer is attached to the customer object,
    // where there could be more than one customer. As a temporary workaround,
    // we will treat the first customer as the primary applicant.
    updateFirstCustomersDisclaimer: (state, { payload }) => {
      state.application.customers[0].customerDisclaimer = {
        ...state.application.customers[0].customerDisclaimer,
        ...payload,
      };
    },
    setRemoveAssetModalOptions: (state, { payload }) => {
      state.removeAssetModalOptions = payload;
    },
  },
  extraReducers: {
    [startApplication.fulfilled]: (state, { payload }) => {
      state.isNewApplication = true;
      state.application = merge(state.application, payload.data.data.application);
      state.application.submissionModal = initialState.application.submissionModal;
      state.gettingApplication = false;
    },
    [startApplication.pending]: (state) => {
      state.application = initialState.application;
    },
    [startApplication.rejected]: (state, { payload }) => {
      state.gettingApplication = false;
    },
    [getApplication.fulfilled]: (state, { payload }) => {
      state.application = merge(state.application, payload?.data?.data);
      state.application.submissionModal = initialState.application.submissionModal;
      state.gettingApplication = false;
      state.gettingQuote = false;
      state.gettingCalculator = false;
    },
    [getApplication.pending]: (state) => {
      state.gettingApplication = true;
      state.application = initialState.application;
    },
    [getApplication.rejected]: (state, { payload }) => {
      state.gettingApplication = false;
    },
    [getApplicationComments.fulfilled]: (state, { payload }) => {
      state.applicationComments = payload?.data?.data;
      state.gettingApplicationComment = false;
    },
    [getApplicationComments.pending]: (state) => {
      state.gettingApplicationComment = true;
    },
    [getApplicationComments.rejected]: (state, { payload }) => {
      state.gettingApplicationComment = false;
    },

    [getApplicationActivityLog.fulfilled]: (state, { payload }) => {
      state.activityLog = payload?.data?.data;
      state.gettingactivityLog = false;
    },
    [getApplicationActivityLog.pending]: (state) => {
      state.gettingactivityLog = true;
    },
    [getApplicationActivityLog.rejected]: (state, { payload }) => {
      state.gettingactivityLog = false;
    },

    [getApplicationServiceLog.fulfilled]: (state, { payload }) => {
      state.applicationServiceLog = payload?.data;
      state.applicationServiceLogLoader = false;
    },
    [getApplicationServiceLog.pending]: (state) => {
      state.applicationServiceLogLoader = true;
    },
    [getApplicationServiceLog.rejected]: (state, { payload }) => {
      state.applicationServiceLog = [];
      state.applicationServiceLogLoader = false;
    },

    [updateApplication.fulfilled]: (state, { payload }) => {
      state.application = merge(state.application, payload.data.data);
      state.application.submissionModal = initialState.application.submissionModal;
      state.gettingApplication = false;
    },
    [updateApplication.pending]: (state) => {},
    [updateApplication.rejected]: (state) => {
      state.gettingApplication = false;
    },
    [updateApplicationField.fulfilled]: (state, { payload }) => {
      state.application = merge(state.application, payload.data.data);
    },
    [updateApplicationField.pending]: (state) => {},
    [updateApplicationField.rejected]: (state) => {},

    [updateApplicationStatus.fulfilled]: (state, { payload }) => {
      state.application.status = payload.data?.data?.status;
      state.updatingApplicationStatus = false;
    },
    [updateApplicationStatus.pending]: (state) => {
      state.updatingApplicationStatus = true;
    },
    [updateApplicationStatus.rejected]: (state) => {
      state.updatingApplicationStatus = false;
    },
    [updateApplicationType.fulfilled]: (state, { payload }) => {
      const updatedApplication = payload?.data?.data;
      // Skip when the response has no body - merging from it would blank the arrays below.
      if (updatedApplication) {
        state.application = merge(state.application, updatedApplication);
        // `merge` keeps existing items when the incoming array is empty - assign so cleared ones drop.
        state.application.entities = updatedApplication.entities ?? [];
        state.application.productsQuotes = updatedApplication.productsQuotes ?? [];
      }
      state.updatingApplicationType = false;
    },
    [updateApplicationType.pending]: (state) => {
      state.updatingApplicationType = true;
    },
    [updateApplicationType.rejected]: (state) => {
      state.updatingApplicationType = false;
    },
    [updateAppStatusToWorkShop.pending]: (state) => {
      state.startApplicationBtnLoader = true;
    },
    [updateAppStatusToWorkShop.rejected]: (state) => {
      state.startApplicationBtnLoader = false;
    },
    [updateAppStatusToWorkShop.fulfilled]: (state) => {
      state.startApplicationBtnLoader = false;
    },
    [submitApplication.pending]: (state) => {
      state.application.submissionModal.submission.status = "pending";
    },
    [submitApplication.rejected]: (state, { payload }) => {
      const updatedSubmissionModalSubmission = {
        status: "rejected",
        message: payload.status_message,
        error: payload.data?.error,
      };
      state.application.submissionModal.submission = updatedSubmissionModalSubmission;
    },
    [submitApplication.fulfilled]: (state, { payload }) => {
      const applicationNo = findKeyValueInData(payload.data?.data, "applicationNo");
      const updatedSubmissionModalSubmission = {
        status: "fulfilled",
        message: payload.status_message,
        applicationNo,
      };
      state.application.submissionModal.submission = updatedSubmissionModalSubmission;
      if (!payload) {
        state.applyApplication = false;
      }
      state.ApplicationBtn = false;
      state.loadingSubmitApplication = false;
    },
    [submitQuote.pending]: (state) => {
      state.application.submissionModal.submission.status = "pending";
    },
    [submitQuote.rejected]: (state, { payload }) => {
      const updatedSubmissionModalSubmission = {
        status: "rejected",
        message: payload.status_message,
        error: payload.data?.error,
      };
      state.application.submissionModal.submission = updatedSubmissionModalSubmission;
    },
    [submitQuote.fulfilled]: (state, { payload }) => {
      const applicationNo = findKeyValueInData(payload.data?.data, "applicationNo");
      const updatedSubmissionModalSubmission = {
        status: "fulfilled",
        message: payload.status_message,
        applicationNo,
      };
      state.application.submissionModal.submission = updatedSubmissionModalSubmission;
      if (!payload) {
        state.applyApplication = false;
      }
      state.ApplicationBtn = false;
      state.loadingSubmitApplication = false;
    },
    [submitApplicationToEmail.fulfilled]: (state, { payload }) => {
      if (!payload.data) {
        state.applyApplication = false;
      }
      state.ApplicationBtn = false;
      state.submittingApplicationToEmail = false;
    },
    [submitApplicationToEmail.pending]: (state) => {
      state.ApplicationBtn = true;
      state.submittingApplicationToEmail = true;
    },
    [submitApplicationToEmail.rejected]: (state) => {
      state.ApplicationBtn = false;
      state.submittingApplicationToEmail = false;
    },
    [getApplicationDownloadUrl.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.downloadApplicationLoader = false;
      state.url = payload.data.data;
    },
    [getApplicationDownloadUrl.pending]: (state) => {
      state.downloadApplicationLoader = true;
    },
    [getApplicationDownloadUrl.rejected]: (state, { payload }) => {
      state.downloadApplicationLoader = false;
      state.errorMessage = payload.message;
    },
    [createProductQuote.pending]: (state) => {
      state.createProductQuoteLoader = true;
    },
    [createProductQuote.rejected]: (state) => {
      state.createProductQuoteLoader = false;
    },
    [createProductQuote.fulfilled]: (state, { payload }) => {
      if (payload.data.data) {
        state.application.productsQuotes = [...state.application.productsQuotes, payload.data.data];
      } else {
        state.application.productsQuotes = [];
      }
      state.createProductQuoteLoader = false;
    },
    [removeProductQuote.pending]: (state) => {
      state.removeProductQuoteLoader = true;
    },
    [removeProductQuote.rejected]: (state) => {
      state.removeProductQuoteLoader = false;
    },
    [removeProductQuote.fulfilled]: (state, { payload }) => {
      state.application.productsQuotes = payload.data.data;
      state.removeProductQuoteLoader = false;
    },
    [updateProductQuote.pending]: (state) => {
      state.updateProductQuoteLoader = true;
    },
    [updateProductQuote.rejected]: (state) => {
      state.updateProductQuoteLoader = false;
    },
    [updateProductQuote.fulfilled]: (state, { payload }) => {
      state.updateProductQuoteLoader = false;
      const updated = payload?.data?.data;
      if (updated?._id && state.application.productsQuotes?.length) {
        const idx = state.application.productsQuotes.findIndex((q) => q._id === updated._id);
        if (idx !== -1) {
          state.application.productsQuotes[idx] = { ...state.application.productsQuotes[idx], ...updated };
        }
      }
      state.productQuoteNoteModal = {
        open: false,
        mode: "save",
        productQuoteId: null,
        initialNotes: "",
      };
    },
    [sendProductQuotesToCustomers.pending]: (state) => {
      state.sendProductQuotesToCustomersLoader = true;
    },
    [sendProductQuotesToCustomers.rejected]: (state) => {
      state.sendProductQuotesToCustomersLoader = false;
    },
    [sendProductQuotesToCustomers.fulfilled]: (state) => {
      state.sendProductQuotesToCustomersLoader = false;
      state.application.productsQuotes = [];
    },
    [setPrimaryProductQuote.pending]: (state, { meta }) => {
      state.setPrimaryProductQuoteLoaderOptions = {
        productQuoteId: meta.arg?.productQuoteId ?? null,
        isLoading: true,
      };
    },
    [setPrimaryProductQuote.rejected]: (state) => {
      state.setPrimaryProductQuoteLoaderOptions = { productQuoteId: null, isLoading: false };
    },
    [setPrimaryProductQuote.fulfilled]: (state, { payload }) => {
      const newQuotes = payload?.data?.data;
      if (Array.isArray(newQuotes)) {
        state.application.productsQuotes = newQuotes;
      }
      state.setPrimaryProductQuoteLoaderOptions = { productQuoteId: null, isLoading: false };
    },
    // Merge server response to preserve local client state (in-flight lookups, search results, cascades).
    [updateAsset.fulfilled]: (state, { payload }) => {
      const stateData = current(state.application);
      const isMainAsset = stateData.asset._id === payload?.data?._id;
      if (isMainAsset) {
        state.application.asset = {
          ...stateData.asset,
          ...payload?.data,
          documents: stateData.asset.documents,
        };
      } else {
        let findindex =
          stateData?.additionalAssets?.length > 0
            ? stateData.additionalAssets.findIndex((el) => el?._id === payload?.data?._id)
            : -1;
        if (findindex !== -1) {
          state.application.additionalAssets[findindex] = {
            ...stateData.additionalAssets[findindex],
            ...payload?.data,
            documents: stateData.additionalAssets[findindex].documents,
          };
        }
      }
    },
    [updateAsset.pending]: (state) => {},
    [updateAsset.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
    },
    [addAdditionalAsset.fulfilled]: (state, { payload }) => {
      state.application.additionalAssets = payload.data?.data?.additionalAssets;
      state.gettingAdditionalAsset = false;
    },
    [addAdditionalAsset.pending]: (state) => {
      state.gettingAdditionalAsset = true;
    },
    [addAdditionalAsset.rejected]: (state) => {
      state.gettingAdditionalAsset = false;
    },
    [removeAdditionalAsset.fulfilled]: (state, { payload }) => {
      state.application.additionalAssets = state.application.additionalAssets?.filter(
        (asset) => asset._id !== payload.data.data?.removedAdditionalAsset,
      );
      state.removeAssetModalOptions.isRemovingAsset = false;
    },
    [removeAdditionalAsset.pending]: (state) => {
      state.removeAssetModalOptions.isRemovingAsset = true;
    },
    [removeAdditionalAsset.rejected]: (state) => {
      state.removeAssetModalOptions.isRemovingAsset = false;
    },
    [getCloneAsset.fulfilled]: (state, action) => {
      state.application.asset = action.payload?.data?.data?.asset;
      state.application.additionalAssets = action.payload?.data?.data?.additionalAssets;
      handleAssetLoaderState(state, action, { loaderKey: "cloneAssetLoaderOptions" });
    },
    [getCloneAsset.pending]: (state, action) => {
      handleAssetLoaderState(state, action, { loaderKey: "cloneAssetLoaderOptions" });
    },
    [getCloneAsset.rejected]: (state, action) => {
      handleAssetLoaderState(state, action, { loaderKey: "cloneAssetLoaderOptions" });
    },
    [makeAssetPrimary.fulfilled]: (state, action) => {
      state.application.asset = action.payload?.data?.data?.asset;
      state.application.additionalAssets = action.payload?.data?.data?.additionalAssets;
      handleAssetLoaderState(state, action, { loaderKey: "makeAssetPrimaryLoaderOptions" });
    },
    [makeAssetPrimary.pending]: (state, action) => {
      handleAssetLoaderState(state, action, { loaderKey: "makeAssetPrimaryLoaderOptions" });
    },
    [makeAssetPrimary.rejected]: (state, action) => {
      handleAssetLoaderState(state, action, { loaderKey: "makeAssetPrimaryLoaderOptions" });
    },

    [updateLoanDetails.fulfilled]: (state, { payload }) => {
      // The saved fee list replaces ours outright, so fees removed from the quote do not linger in the store.
      state.application.loanDetails = mergeWith(state.application.loanDetails, payload.data.data, replaceArrays);
    },

    [updateLoanDetails.pending]: (state) => {},
    [updateLoanDetails.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
    },

    [createCustomer.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      const previousCanEditApplication = state.application?.canEditApplication;
      state.application = {
        ...payload.data.data,
        canEditApplication: previousCanEditApplication,
      };
      state.application.submissionModal = initialState.application.submissionModal;
    },
    [createCustomer.pending]: (state) => {},
    [createCustomer.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
    },

    [removeCustomer.fulfilled]: (state, { payload }) => {
      const previousCanEditApplication = state.application?.canEditApplication;
      state.application = {
        ...payload.data.data,
        canEditApplication: previousCanEditApplication,
      };
      state.application.submissionModal = initialState.application.submissionModal;
    },
    [removeCustomer.pending]: (state) => {},
    [removeCustomer.rejected]: (state, { payload }) => {
      state.isFetching = false;
    },

    [getAddress.fulfilled]: (state, { payload }) => {
      state.successMessage = payload?.message;
      state.address = payload?.data?.data ?? {};
    },
    [getAddress.pending]: (state) => {},
    [getAddress.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
    },
    [addAddress.pending]: (state) => {
      state.gettingAddressDetails = true;
    },
    [addAddress.rejected]: (state, { payload }) => {
      state.gettingAddressDetails = false;
    },
    [addAddress.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.gettingAddressDetails = false;
      state.successMessage = payload.message;
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === payload?.data.data._id);

      state.application.customers[findindex] = payload.data.data;
    },

    [updateAddress.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.successMessage = payload.message;
      const updatedUser = payload?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === updatedUser?._id);
      state.application.customers[findindex] = updatedUser;
    },
    [deleteAddress.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.successMessage = payload.message;
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === payload?.data?._id);
      state.application.customers[findindex].addresses = payload?.data?.addresses;
    },

    [addPhoneNumber.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.successMessage = payload.message;
      if (payload?.data?.data?.abn) {
        state.application.entities.forEach((entity) => {
          if (entity._id === payload?.data?.data?._id) {
            entity.phoneNumbers = payload?.data?.data?.phoneNumbers;
          }
        });
      } else {
        state.application.customers.forEach((customer) => {
          if (customer._id === payload?.data?.data?._id) {
            customer.phoneNumbers = payload?.data?.data?.phoneNumbers;
          }
        });
      }
    },
    [addEmail.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.successMessage = payload.message;

      if (payload?.data?.data?.abn) {
        state.application.entities.forEach((entity) => {
          if (entity._id === payload?.data?.data?._id) {
            entity.emails = payload?.data?.data?.emails;
          }
        });
      } else {
        state.application.customers.forEach((customer) => {
          if (customer._id === payload?.data?.data?._id) {
            customer.emails = payload?.data?.data?.emails;
          }
        });
      }
    },

    [saveCustomerProfile.fulfilled]: (state, { payload }) => {
      state.application.customers.filter(
        (customer) => customer._id === payload?.data.data.customer,
      )[0].customerProfile = merge(
        state.application.customers.filter((customer) => customer._id === payload?.data.data.customer)[0]
          .customerProfile,
        payload.data.data,
      );
    },
    [saveCustomerProfile.pending]: (state) => {},
    [saveCustomerProfile.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
    },
    [resetExpensesAndLiabilitiesShares.fulfilled]: (state, { payload }) => {
      const updatedCustomer = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === updatedCustomer?._id);
      if (findindex === -1) return;
      state.application.customers[findindex].expenses = updatedCustomer.expenses;
      state.application.customers[findindex].liabilities = updatedCustomer.liabilities;
      // Do not update loader state due to loader has to wait for 2nd api response
    },
    [resetExpensesAndLiabilitiesShares.pending]: (state) => {
      state.isConfirmingShareType = true;
    },
    [resetExpensesAndLiabilitiesShares.rejected]: (state) => {
      state.isConfirmingShareType = false;
    },

    [createEntity.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.application.entity = merge(state.application.entities, payload.data.data.entities);
      state.application.customers = merge([], state.application.customers, payload.data.data.customers);
      state.gettingEntityDetails = false;
    },
    [createEntity.pending]: (state) => {},
    [createEntity.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
    },

    [removeEntity.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.application.entities = payload.data.data;
      state.gettingEntityDetails = false;
      state.removingEntityOptions = {
        isLoading: false,
        entityId: "",
      };
      state.removingEntityOptions = {
        isLoading: false,
        entityId: "",
      };
    },
    [removeEntity.pending]: (state, payload) => {
      const entityId = payload?.meta?.arg?.entityId;
      state.removingEntityOptions = {
        isLoading: true,
        entityId: entityId,
      };
    },
    [removeEntity.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
      state.removingEntityOptions = {
        isLoading: false,
        entityId: "",
      };
    },
    [createEmployer.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.application.customers.filter((customer) => customer._id === payload?.data?.data[0].customer)[0].employers =
        merge(
          state.application.customers.filter((customer) => customer._id === payload?.data?.data[0].customer)[0]
            .employers,
          payload.data.data,
        );

      state.gettingEntityDetails = false;
    },
    [createEmployer.pending]: (state) => {
      state.gettingEntityDetails = true;
    },
    [createEmployer.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
      state.gettingEntityDetails = false;
    },

    [saveEmployer.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.application.customers.filter((customer) => customer._id === payload?.data.data[0].customer)[0].employers =
        merge(
          state.application.customers.filter((customer) => customer._id === payload?.data.data[0].customer)[0]
            .employers,
          payload.data.data,
        );

      state.gettingEntityDetails = false;
    },
    [saveEmployer.pending]: (state) => {},
    [saveEmployer.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
    },

    [getASICData.fulfilled]: (state, { payload }) => {
      state.gettingASICData = false;
      if (!payload?.data) return;
      state.application.entity = merge(state.application.entities, payload.data.entities);
      state.application.customers = merge([], state.application.customers, payload.data.customers);
    },
    [getASICData.rejected]: (state, { payload }) => {
      state.gettingASICData = false;
      state.errorMessage = payload?.message;
    },

    [updateEntity.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      const data = current(state.application.entities);
      let findindex = data.findIndex((el) => el?._id === payload?.data?.data?._id);
      state.application.entities[findindex] = payload?.data?.data;
    },
    [updateEntity.pending]: (state) => {},
    [updateEntity.rejected]: (state, { payload }) => {
      state.errorMessage = payload?.message;
    },

    [getEntity.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.entity = Object.assign({}, state.entity, payload.data.data);
    },
    [getEntity.pending]: (state) => {},
    [getEntity.rejected]: (state) => {},

    [startVerification.pending]: (state) => {},
    [startVerification.fulfilled]: (state, { payload }) => {
      state.idVerificationRequest = true;
    },
    [startVerification.rejected]: (state) => {},

    [getIdentityVerification.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.application.customers.filter((customer) => customer._id === payload?.data?._id)[0] = merge(
        state.application.customers.filter((customer) => customer._id === payload?.data?._id)[0],
        payload?.data,
      );
      JSON.stringify(state.application.customers.filter((customer) => customer._id === payload?.data?._id)[0], null, 2);
    },
    [getIdentityVerification.pending]: (state) => {},
    [getIdentityVerification.rejected]: (state) => {},

    [getDocuments.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.application.customers.filter((customer) => customer._id === payload.data.data._id)[0] = merge(
        state.application.customers.filter((customer) => customer._id === payload.data.data._id)[0],
        payload.data.data,
      );
    },
    [deleteDocument.pending]: (state) => {
      state.isDeletingDocument = true;
    },
    [deleteDocument.rejected]: (state) => {
      state.isDeletingDocument = false;
    },
    [deleteDocument.fulfilled]: (state, { payload }) => {
      const data = payload?.data?.data;
      if (data?.entityId) {
        const foundEntity = state.application.entities?.find((entity) => entity?._id === data?.entityId);
        if (foundEntity) {
          const newDocList = foundEntity.documents.filter((document) => document?._id !== data?.deletedDocId);
          foundEntity.documents = newDocList;
        }
      } else if (data?.customerId) {
        const foundCustomer = state.application.customers?.find((customer) => customer?._id === data?.customerId);
        if (foundCustomer) {
          const newDocList = foundCustomer.documents.filter((document) => document?._id !== data?.deletedDocId);
          foundCustomer.documents = newDocList;
        }
      } else if (data?.assetId) {
        const stateData = current(state.application);
        const isMainAsset = stateData.asset._id === data?.assetId;
        if (isMainAsset) {
          const newDocList = state.application.asset.documents.filter(
            (document) => document?._id !== data?.deletedDocId,
          );
          state.application.asset.documents = newDocList;
        } else {
          const findindex = stateData.additionalAssets?.findIndex((el) => el?._id === data?.assetId);
          if (findindex !== -1) {
            const newDocList = state.application.additionalAssets[findindex].documents.filter(
              (document) => document?._id !== data?.deletedDocId,
            );
            state.application.additionalAssets[findindex].documents = newDocList;
          }
        }
      }
      state.isDeletingDocument = false;
    },
    [getCreditFile.pending]: (state) => {
      state.gettingCreditProfile = true;
      state.gettingEquifaxCreditFile = true;
    },
    [getCreditFile.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.gettingCreditProfile = false;
      state.application.customers.filter((customer) => customer._id === payload?.data?.data._id)[0] = merge(
        state.application.customers.filter((customer) => customer._id === payload?.data?.data._id)[0],
        payload.data.data,
      );
    },
    [getCreditFile.rejected]: (state) => {
      state.gettingCreditProfile = false;
    },
    [getCommercialCreditFile.pending]: (state) => {
      state.gettingEquifaxCreditCompanyFile = true;
    },
    [getCommercialCreditFile.fulfilled]: (state, { payload }) => {
      state.successMessage = payload?.data?.status_message;
      state.gettingCreditProfile = false;
      state.gettingEquifaxCreditCompanyFile = false;

      const updatedEntity = payload?.data?.data?.commercialCreditProfile;
      const entity = state.application.entities?.find((el) => el?._id === updatedEntity?.entity);
      if (entity) {
        entity.creditProfile = updatedEntity;
      }
    },
    [getCommercialCreditFile.rejected]: (state) => {
      state.gettingCreditProfile = false;
      state.gettingEquifaxCreditCompanyFile = false;
    },
    [getEntityCommercialCreditCheck.pending]: (state, { meta }) => {
      setCommercialCreditCheckLoading(state, meta.arg, true);
    },
    [getEntityCommercialCreditCheck.fulfilled]: (state, { payload, meta }) => {
      setCommercialCreditCheckLoading(state, meta.arg, false);

      const document = payload?.data?.data;
      const entity = state.application.entities?.find((el) => el?._id === meta.arg.entityId);
      if (entity && document?._id) {
        entity.documents = [...(entity.documents ?? []), document];
      }
    },
    [getEntityCommercialCreditCheck.rejected]: (state, { meta }) => {
      setCommercialCreditCheckLoading(state, meta.arg, false);
    },

    [requestEmploymentIncomeVerification.pending]: (state, { meta }) => {
      state.requestingEmploymentIncomeVerification[meta.arg.customerId] = true;
    },
    [requestEmploymentIncomeVerification.fulfilled]: (state, { payload, meta }) => {
      delete state.requestingEmploymentIncomeVerification[meta.arg.customerId];

      // The response can land after the broker has opened another application.
      if (state.application?._id !== meta.arg.applicationId) return;

      const { employmentIncomeVerification, document, employers } = payload?.data?.data ?? {};
      const customer = state.application.customers?.find((el) => el?._id === meta.arg.customerId);
      if (!customer || !employmentIncomeVerification?._id) return;

      customer.employmentIncomeVerification = employmentIncomeVerification;
      if (document?._id) {
        customer.documents = [...(customer.documents ?? []), document];
      }
      // Sent only when the match added employers or filled in the customer's: all of them, as saved.
      if (Array.isArray(employers)) {
        customer.employers = employers;
      }
    },
    [requestEmploymentIncomeVerification.rejected]: (state, { meta }) => {
      delete state.requestingEmploymentIncomeVerification[meta.arg.customerId];
    },

    [getCreditProfile.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      if (payload.data?._id) state.creditProfile[payload.data?.customer] = payload?.data;
    },
    [sendBankStatementsLink.pending]: (state) => {
      state.bankStatementsLoading = true;
    },
    [sendBankStatementsLink.fulfilled]: (state, { payload }) => {
      state.document[payload.data.data.documentId] = payload.data.data.url;
      state.bankStatementsLoading = false;
      state.bankStatementsRequest = true;
    },
    [sendBankStatementsLink.rejected]: (state) => {
      state.bankStatementsLoading = false;
    },

    [getFileUrl.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      merge(state.document[payload.data.data.documentId], payload.data.data.url);
    },
    [updateNotes.fulfilled]: (state, { payload }) => {
      state.successMessage = payload.message;
      state.application.notes = payload?.data?.data;
    },

    [uploadDocument.fulfilled]: (state, { payload }) => {
      const updatedCustomer = payload?.data?.data;
      const customers = state.application?.customers;
      if (updatedCustomer && Array.isArray(customers)) {
        const existingCustomer = customers.find((customer) => customer._id === updatedCustomer._id);
        if (existingCustomer) merge(existingCustomer, updatedCustomer);
      }
      if (state.uploadPrivacyLoading === true) state.uploadPrivacyLoading = false;

      if (state.documentUploadLoading === true) {
        state.documentUploadLoading = false;
      }
      if (state.uploadIDLoading === true) state.uploadIDLoading = false;
    },
    [uploadDocument.pending]: (state) => {
      state.documentUploadLoding = true;
    },
    [uploadDocument.rejected]: (state) => {
      state.documentUploadLoding = false;
      state.uploadPrivacyLoading = false;
      state.uploadIDLoading = false;
    },
    [uploadEntityDocument.fulfilled]: (state, { payload }) => {
      const updatedEntity = payload?.data?.data;
      const data = current(state.application.entities);
      let findindex = data.findIndex((el) => el?._id === updatedEntity?._id);
      state.application.entities[findindex].documents = updatedEntity?.documents;
      state.entityDocumentUploadLoadingId = "";
    },
    [uploadEntityDocument.pending]: (state, payload) => {
      const entityId = payload?.meta?.arg?.formData?.get("entityId");
      state.entityDocumentUploadLoadingId = entityId;
    },
    [uploadEntityDocument.rejected]: (state) => {
      state.entityDocumentUploadLoadingId = "";
    },
    [uploadAssetInvoice.fulfilled]: (state, { payload }) => {
      // Need to add logic for update redux state for asset
      const stateData = current(state.application);
      const isMainAsset = stateData.asset._id === payload?.data?.data?._id;
      if (isMainAsset) {
        state.application.asset.documents = payload?.data?.data?.documents;
      } else {
        let findindex = stateData.additionalAssets.findIndex((el) => el?._id === payload?.data?.data?._id);
        if (findindex !== -1) {
          state.application.additionalAssets[findindex].documents = payload?.data?.data?.documents;
        }
      }
      state.assetDocumentUploadLoading = false;
    },
    [uploadAssetInvoice.pending]: (state) => {
      state.assetDocumentUploadLoading = true;
    },
    [uploadAssetInvoice.rejected]: (state) => {
      state.assetDocumentUploadLoading = false;
    },
    [requestAssetInvoice.fulfilled]: (state) => {
      state.requestAssetInvoiceLoading = false;
    },
    [requestAssetInvoice.pending]: (state) => {
      state.requestAssetInvoiceLoading = true;
    },
    [requestAssetInvoice.rejected]: (state) => {
      state.requestAssetInvoiceLoading = false;
    },
    [removeAssetInvoice.fulfilled]: (state, { payload }) => {
      // Need to add logic for update redux state for asset
      const stateData = current(state.application);
      const isMainAsset = stateData.asset._id === payload?.data?.data?._id;
      if (isMainAsset) {
        state.application.asset.documents = payload?.data?.data?.documents;
      } else {
        let findindex = stateData.additionalAssets.findIndex((el) => el?._id === payload?.data?.data?._id);
        if (findindex !== -1) {
          state.application.additionalAssets[findindex].documents = payload?.data?.data?.documents || [];
        }
      }
      state.assetDocumentRemovingLoading = false;
    },
    [removeAssetInvoice.pending]: (state) => {
      state.assetDocumentRemovingLoading = true;
    },
    [removeAssetInvoice.rejected]: (state) => {
      state.assetDocumentRemovingLoading = false;
    },
    [createFinanceItem.fulfilled]: (state, { payload }) => {
      state.gettingCreditProfile = false;
      state.application.customers.filter(
        (customer) => customer._id === payload.data.data[Object.keys(payload.data.data)[0]].customer,
      )[0][Object.keys(payload.data.data)[0]] = payload.data.data[Object.keys(payload.data.data)[0]];

      JSON.stringify(
        state.application.customers.filter(
          (customer) => customer._id === payload.data.data[Object.keys(payload.data.data)[0]].customer,
        )[0].null,
        2,
      );
    },
    [deleteFinanceItem.fulfilled]: (state, { payload }) => {
      state.gettingCreditProfile = false;
      state.application.customers.filter(
        (customer) => customer._id === payload.data.data[Object.keys(payload.data.data)[0]].customer,
      )[0][Object.keys(payload.data.data)[0]] = payload.data.data[Object.keys(payload.data.data)[0]];

      JSON.stringify(
        state.application.customers.filter(
          (customer) => customer._id === payload.data.data[Object.keys(payload.data.data)[0]].customer,
        )[0].null,
        2,
      );
    },
    [updateCreditProfile.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.updatingCreditScoreModeCusId = null;
      state.successMessage = payload.message;

      const newCreditProfile = payload?.data?.data;
      if (!newCreditProfile) return;

      const customers = current(state.application.customers);
      const customerIndex = customers.findIndex((customer) => customer?._id === newCreditProfile.customer);
      if (customerIndex === -1) return;

      const existingCreditProfile = state.application.customers[customerIndex].creditProfile;
      // Merge into the existing profile when present, otherwise attach the new record as-is.
      state.application.customers[customerIndex].creditProfile = existingCreditProfile
        ? merge({}, existingCreditProfile, newCreditProfile)
        : newCreditProfile;
    },
    [updateCreditProfile.rejected]: (state) => {
      state.updatingCreditScoreModeCusId = null;
    },
    [createAssets.pending]: (state) => {
      state.personalFinLoaders.addAssetLoader = true;
    },
    [createAssets.fulfilled]: (state, { payload }) => {
      const assetsList = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === assetsList[0]?.customer);
      state.application.customers[findindex].customerAssets = assetsList;
      state.personalFinLoaders = {
        id: "",
        addAssetLoader: false,
      };
    },
    [createAssets.rejected]: (state) => {
      state.personalFinLoaders = {
        id: "",
        addAssetLoader: false,
      };
    },
    [updateAssets.pending]: (state) => {},
    [updateAssets.fulfilled]: (state, { payload }) => {
      const assetsList = payload?.data || [];
      const data = current(state.application.customers);
      let findindex = data?.findIndex((el) => el?._id === assetsList?.[0]?.customer);
      state.application.customers[findindex].customerAssets = assetsList;
    },
    [updateAssets.rejected]: (state) => {},
    [deleteAssets.pending]: (state) => {
      state.personalFinLoaders.removeAssetLoader = true;
    },
    [deleteAssets.fulfilled]: (state, action) => {
      setCustomerListAfterDelete(state, action, "customerAssets");
      state.personalFinLoaders = {
        id: "",
        removeAssetLoader: false,
      };
    },
    [deleteAssets.rejected]: (state) => {
      state.personalFinLoaders = {
        id: "",
        removeAssetLoader: false,
      };
    },
    [createExpense.pending]: (state) => {
      state.personalFinLoaders.addExpenseLoader = true;
    },
    [createExpense.fulfilled]: (state, { payload }) => {
      const expensesList = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === expensesList[0]?.customer);
      state.application.customers[findindex].expenses = expensesList;
      state.personalFinLoaders = {
        id: "",
        addExpenseLoader: false,
      };
    },
    [createExpense.rejected]: (state) => {
      state.personalFinLoaders = {
        id: "",
        addExpenseLoader: false,
      };
    },
    [updateExpense.pending]: (state) => {},
    [updateExpense.fulfilled]: (state, { payload }) => {
      const expensesList = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === expensesList[0]?.customer);
      state.application.customers[findindex].expenses = expensesList;
    },
    [updateExpense.rejected]: (state) => {},
    [deleteExpense.pending]: (state) => {
      state.personalFinLoaders.removeExpenseLoader = true;
    },
    [deleteExpense.fulfilled]: (state, action) => {
      setCustomerListAfterDelete(state, action, "expenses");
      state.personalFinLoaders = {
        id: "",
        removeExpenseLoader: false,
      };
    },
    [deleteExpense.rejected]: (state) => {
      state.personalFinLoaders = {
        id: "",
        removeExpenseLoader: false,
      };
    },
    [deleteEmployer.pending]: (state) => {
      state.removeEmployerLoader = true;
    },
    [deleteEmployer.fulfilled]: (state, { payload }) => {
      state.removeEmployerLoader = false;
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === payload?.data?.data?._id);
      state.application.customers[findindex].employers = payload?.data?.data?.employers;
    },
    [deleteEmployer.rejected]: (state) => {
      state.removeEmployerLoader = false;
    },
    [createIncome.pending]: (state) => {
      state.personalFinLoaders.addIncomeLoader = true;
    },
    [createIncome.fulfilled]: (state, { payload }) => {
      const incomeList = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === incomeList[0]?.customer);
      state.application.customers[findindex].income = incomeList;
      state.personalFinLoaders = {
        id: "",
        addIncomeLoader: false,
      };
    },
    [createIncome.rejected]: (state) => {
      state.personalFinLoaders = {
        id: "",
        addIncomeLoader: false,
      };
    },
    [updateIncome.pending]: (state) => {},
    [updateIncome.fulfilled]: (state, { payload }) => {
      const incomeList = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === incomeList[0]?.customer);
      state.application.customers[findindex].income = incomeList;
    },
    [updateIncome.rejected]: (state) => {},
    [deleteIncome.pending]: (state) => {
      state.personalFinLoaders.removeIncomeLoader = true;
    },
    [deleteIncome.fulfilled]: (state, action) => {
      setCustomerListAfterDelete(state, action, "income");
      state.personalFinLoaders = {
        id: "",
        removeIncomeLoader: false,
      };
    },
    [deleteIncome.rejected]: (state) => {
      state.personalFinLoaders = {
        id: "",
        removeIncomeLoader: false,
      };
    },
    [submitPrivacy.pending]: (state) => {
      state.submittingSignature = true;
    },
    [submitPrivacy.fulfilled]: (state) => {
      state.submittingSignature = false;
    },
    [submitPrivacy.rejected]: (state) => {
      state.submittingSignature = false;
    },
    [submitDisclosure.pending]: (state) => {
      state.submittingSignature = true;
    },
    [submitDisclosure.fulfilled]: (state) => {
      state.submittingSignature = false;
    },
    [submitDisclosure.rejected]: (state) => {
      state.submittingSignature = false;
    },
    [createLiabilities.pending]: (state) => {
      state.personalFinLoaders.addLiabilityLoader = true;
    },
    [createLiabilities.rejected]: (state) => {
      state.personalFinLoaders = {
        id: "",
        addLiabilityLoader: false,
      };
    },
    [createLiabilities.fulfilled]: (state, { payload }) => {
      const liabilityList = payload?.data?.data || [];

      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === liabilityList[0]?.customer);
      state.application.customers[findindex].liabilities = liabilityList;
      state.personalFinLoaders = {
        id: "",
        addLiabilityLoader: false,
      };
    },
    [updateLiabilities.pending]: (state) => {},
    [updateLiabilities.fulfilled]: (state, { payload }) => {
      const liabilityList = payload?.data?.data || [];

      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === liabilityList[0]?.customer);
      state.application.customers[findindex].liabilities = liabilityList;
    },
    [updateLiabilities.rejected]: (state) => {},
    [deleteLiabilities.pending]: (state) => {
      state.personalFinLoaders.removeLiabilityLoader = true;
    },
    [deleteLiabilities.fulfilled]: (state, action) => {
      setCustomerListAfterDelete(state, action, "liabilities");
      state.personalFinLoaders = {
        id: "",
        removeLiabilityLoader: false,
      };
    },
    [deleteLiabilities.rejected]: (state) => {
      state.personalFinLoaders = {
        id: "",
        removeLiabilityLoader: false,
      };
    },
    [getAssetLookupProvider.pending]: (state) => {
      state.assetLookupProviderLoading = true;
    },
    [getAssetLookupProvider.fulfilled]: (state, action) => {
      state.assetLookupProvider = action.payload?.data?.data?.provider || "GLASSES";
      state.assetLookupProviderResolved = true;
      state.assetLookupProviderLoading = false;
      state.assetLookupProviderSettled = true;
    },
    [getAssetLookupProvider.rejected]: (state) => {
      // Left unresolved so the next asset card retries; Glasses is the pre-AutoGrab default.
      state.assetLookupProvider = "GLASSES";
      state.assetLookupProviderLoading = false;
      // Settled all the same: the fallback is an answer, and anything waiting on one must not hang
      // on an endpoint that may keep failing.
      state.assetLookupProviderSettled = true;
    },
    [getListOfMakes.pending]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "makeLoader",
          listKey: "listOfMakes",
        },
        "pending",
      );
    },

    [getListOfMakes.rejected]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "makeLoader",
          listKey: "listOfMakes",
        },
        "rejected",
      );
    },

    [getListOfMakes.fulfilled]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "makeLoader",
          listKey: "listOfMakes",
        },
        "fulfilled",
      );
    },

    [getListOfMakesYearFromTo.pending]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "makeLoader",
          listKey: "listOfMakes",
        },
        "pending",
      );
    },

    [getListOfMakesYearFromTo.rejected]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "makeLoader",
          listKey: "listOfMakes",
        },
        "rejected",
      );
    },

    [getListOfMakesYearFromTo.fulfilled]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "makeLoader",
          listKey: "listOfMakes",
        },
        "fulfilled",
      );
    },
    [getListOfModels.pending]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "modelLoader",
          listKey: "listOfModels",
        },
        "pending",
      );
    },

    [getListOfModels.rejected]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "modelLoader",
          listKey: "listOfModels",
        },
        "rejected",
      );
    },
    [getListOfModels.fulfilled]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "modelLoader",
          listKey: "listOfModels",
        },
        "fulfilled",
      );
    },
    [getListOfModelsYearFromTo.pending]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "modelLoader",
          listKey: "listOfModels",
        },
        "pending",
      );
    },

    [getListOfModelsYearFromTo.rejected]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "modelLoader",
          listKey: "listOfModels",
        },
        "rejected",
      );
    },
    [getListOfModelsYearFromTo.fulfilled]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "modelLoader",
          listKey: "listOfModels",
        },
        "fulfilled",
      );
    },
    [getListOfVariants.pending]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "variantLoader",
          listKey: "listOfVariants",
        },
        "pending",
      );
    },

    [getListOfVariants.rejected]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "variantLoader",
          listKey: "listOfVariants",
        },
        "rejected",
      );
    },
    [getListOfVariants.fulfilled]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "variantLoader",
          listKey: "listOfVariants",
        },
        "fulfilled",
      );
    },

    [getListOfVariantsYearFromTo.pending]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "variantLoader",
          listKey: "listOfVariants",
        },
        "pending",
      );
    },

    [getListOfVariantsYearFromTo.rejected]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "variantLoader",
          listKey: "listOfVariants",
        },
        "rejected",
      );
    },
    [getListOfVariantsYearFromTo.fulfilled]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "variantLoader",
          listKey: "listOfVariants",
        },
        "fulfilled",
      );
    },
    [getListOfNVICForAdvancedSearch.pending]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "NVICLoader",
          listKey: "NVICList",
        },
        "pending",
      );
    },

    [getListOfNVICForAdvancedSearch.rejected]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "NVICLoader",
          listKey: "NVICList",
        },
        "rejected",
      );
    },
    [getListOfNVICForAdvancedSearch.fulfilled]: (state, action) => {
      handleGlassesFieldsListState(
        state,
        action,
        {
          loaderKey: "NVICLoader",
          listKey: "NVICList",
        },
        "fulfilled",
      );
    },
    [searchVehiclesByKeyword.pending]: (state, action) => {
      const target = getAssetTarget(state, action);
      if (!target) return;
      // Also the out-of-order guard for the handlers below.
      target.plainTextSearchTerm = action.meta.arg?.keyword;
      target.plainTextSearchLoader = true;
      target.plainTextSearchError = false;
    },

    [searchVehiclesByKeyword.rejected]: (state, action) => {
      const target = getAssetTarget(state, action);
      if (!target || target.plainTextSearchTerm !== action.meta.arg?.keyword) return;
      target.plainTextSearchLoader = false;
      target.plainTextSearchList = [];
      // Distinguishes a failed lookup from a search that matched nothing.
      target.plainTextSearchError = true;
    },

    [searchVehiclesByKeyword.fulfilled]: (state, action) => {
      const target = getAssetTarget(state, action);
      // Drop a stale response from an earlier keystroke.
      if (!target || target.plainTextSearchTerm !== action.meta.arg?.keyword) return;
      const data = action.payload?.data?.data;
      target.plainTextSearchLoader = false;
      target.plainTextSearchError = false;
      target.plainTextSearchList = Array.isArray(data) ? data : [];
    },

    [getDetailsSpecificationAll.pending]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "nvicLoaderData",
        dataKey: "NVICData",
      });
    },
    [getDetailsSpecificationAll.rejected]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "nvicLoaderData",
        dataKey: "NVICData",
      });
    },

    [getDetailsSpecificationAll.fulfilled]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "nvicLoaderData",
        dataKey: "NVICData",
      });
    },

    [getVehicleSpecification.pending]: (state, action) => {
      const target = getAssetTarget(state, action);
      if (!target) return;
      // Also the out-of-order guard for the handlers below: picking is a round trip now, so two quick
      // picks race, and the loser must not be the one written onto the asset.
      target.requestedVehicleId = action.meta.arg?.vehicleId;
      target.nvicLoaderData = true;
      target.NVICData = null;
    },
    [getVehicleSpecification.rejected]: (state, action) => {
      const target = getAssetTarget(state, action);
      if (!target || target.requestedVehicleId !== action.meta.arg?.vehicleId) return;
      target.nvicLoaderData = false;
      target.NVICData = null;
    },
    [getVehicleSpecification.fulfilled]: (state, action) => {
      const target = getAssetTarget(state, action);
      if (!target || target.requestedVehicleId !== action.meta.arg?.vehicleId) return;
      target.nvicLoaderData = false;
      target.NVICData = action.payload?.data?.data;
    },

    [getVehicleLookup.pending]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "searchVehicleLoader",
        dataKey: "vehicleData",
      });
      // Clear last search's outcome so the manual-entry note doesn't linger over a search in flight.
      const target = getAssetTargetFromQuery(state, action);
      if (target) target.isNVICAbsence = false;
    },
    [getVehicleLookup.fulfilled]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "searchVehicleLoader",
      });
      const target = getAssetTargetFromQuery(state, action);
      if (!target) return;
      const payloadData = action.payload?.data?.data;
      target.vehicleData = payloadData?.vehicleData;
      target.isNVICAbsence = Boolean(payloadData?.isNVICAbsence);
    },
    [getVehicleLookup.rejected]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "searchVehicleLoader",
        dataKey: "vehicleData",
      });
      // A failed lookup leaves the broker with no vehicle to select, so point them at manual entry.
      const target = getAssetTargetFromQuery(state, action);
      if (target) target.isNVICAbsence = true;
    },
    [getVehiclePPSRSearch.pending]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "searchPPSRLoader",
        dataKey: "blueflagPPSR",
      });
    },
    [getVehiclePPSRSearch.fulfilled]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "searchPPSRLoader",
      });
      const stateData = current(state.application);
      const assetId = action.meta.arg?.includes("assetId") ? action.meta.arg.split("assetId=")[1] : undefined;
      const isMainAsset = stateData.asset._id === assetId;
      const payloadData = action.payload?.data?.data;
      const result = payloadData.result[0].search_result;

      if (isMainAsset) {
        if (!state.application.asset.blueflagPPSR) {
          state.application.asset.blueflagPPSR = { search: null, nevdisData: null };
        }
        state.application.asset.blueflagPPSR["search"] = result.result_details;
        state.application.asset.blueflagPPSR["nevdisData"] = result.nevdis_data;
      } else {
        let findindex = stateData.additionalAssets.findIndex((el) => el?._id === assetId);
        if (findindex !== -1) {
          if (!state.application.additionalAssets[findindex].blueflagPPSR) {
            state.application.additionalAssets[findindex].blueflagPPSR = { search: null, nevdisData: null };
          }
          state.application.additionalAssets[findindex].blueflagPPSR["search"] = result.result_details;
          state.application.additionalAssets[findindex].blueflagPPSR["nevdisData"] = result.nevdis_data;
        }
      }
    },
    [getVehiclePPSRSearch.rejected]: (state, action) => {
      handleAssetFieldsListState(state, action, {
        loaderKey: "searchPPSRLoader",
      });
    },
    [updateApplicationAgreement.pending]: (state) => {},
    [updateApplicationAgreement.fulfilled]: (state, { payload }) => {
      state.application.introducerDeclarationAgree = payload?.data?.data?.introducerDeclarationAgree;
    },
    [updateApplicationAgreement.rejected]: (state) => {
      state.application.introducerDeclarationAgree = false;
    },
    [getApplicationDocumentPreview.fulfilled]: (state, { payload }) => {
      state.documentPreview = payload?.data?.data?.document;
    },
    [getApplicationDocumentPreview.rejected]: (state) => {
      state.privacyLoading = false;
      state.disclosureLoader = false;
    },
    [sendDocumentSignInLink.pending]: (state) => {
      state.documentLinkSentLoader = true;
    },
    [sendDocumentSignInLink.fulfilled]: (state) => {
      state.documentLinkSentLoader = false;
      state.showPreviewDocument = {
        isOpen: false,
        customerId: "",
        templateType: "",
        disclosureInfo: null,
      };
    },
    [sendDocumentSignInLink.rejected]: (state) => {
      state.documentLinkSentLoader = false;
    },
    [updateNccpDeclaration.pending]: (state) => {},
    [updateNccpDeclaration.fulfilled]: (state, { payload }) => {},
    [updateNccpDeclaration.rejected]: (state) => {},
    [updateAcknowledgement.pending]: (state) => {
      state.application.submissionModal.acknowledgement = "pending";
    },
    [updateAcknowledgement.fulfilled]: (state) => {
      state.application.submissionModal.acknowledgement = "fulfilled";
    },
    [updateAcknowledgement.rejected]: (state) => {
      state.application.submissionModal.acknowledgement = "rejected";
    },
    [customerLogin.fulfilled]: (state, { payload }) => {
      state.errorMessage = null;
      localStorage.customer_token = payload.data.data.token;
      state.application = {
        customers: [payload.data.data?.customer],
      };
    },
    [customerLogin.pending]: (state) => {
      state.isFetching = true;
    },
    [customerLogin.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = payload.message;
    },
    [sendCustomerDetailsLinkToCustomer.fulfilled]: (state, { payload }) => {
      const updatedUser = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === updatedUser?._id);
      state.application.customers[findindex] = updatedUser;
    },
    [sendDocumentRequestLinkToCustomer.fulfilled]: (state, { payload }) => {
      const updatedUser = payload?.data?.data;
      if (!updatedUser?._id) return;
      const data = current(state.application.customers);
      const findindex = data.findIndex((el) => el?._id === updatedUser?._id);
      if (findindex !== -1) state.application.customers[findindex] = updatedUser;
    },
    [saveCustomerDetails.fulfilled]: (state, { payload }) => {
      const updatedUser = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === updatedUser?._id);
      state.application.customers[findindex] = updatedUser;
    },
    [getCloneApplication.fulfilled]: (state) => {
      state.gettingClonedApplication = false;
    },
    [getCloneApplication.pending]: (state) => {
      state.gettingClonedApplication = true;
    },
    [getCloneApplication.rejected]: (state) => {
      state.gettingClonedApplication = false;
    },
    [confirmCustomerData.fulfilled]: (state, { payload }) => {
      const updatedUser = payload?.data?.data || [];
      const data = current(state.application.customers);
      let findindex = data.findIndex((el) => el?._id === updatedUser?._id);
      state.application.customers[findindex] = updatedUser;
      state.confirmCustomerDataLoader = false;
    },
    [confirmCustomerData.pending]: (state) => {
      state.confirmCustomerDataLoader = true;
    },
    [confirmCustomerData.rejected]: (state) => {
      state.confirmCustomerDataLoader = false;
    },
    [getApplicationDocuments.pending]: (state) => {
      state.application.documents = {
        status: "pending",
      };
    },
    [getApplicationDocuments.fulfilled]: (state, { payload }) => {
      const updatedDocuments = {
        ...payload.data.data,
        status: "fulfilled",
      };
      state.application.documents = updatedDocuments;
    },
    [getApplicationDocuments.rejected]: (state) => {
      state.application.documents = {
        status: "rejected",
      };
    },
    [createInsuranceQuote.pending]: (state, { meta }) => {
      state.gettingInsuranceQuote[meta.arg.assetId] = true;
    },
    [createInsuranceQuote.fulfilled]: (state, { payload, meta }) => {
      delete state.gettingInsuranceQuote[meta.arg.assetId];
      const stateData = current(state.application);
      const isMainAsset = stateData.asset._id === payload?.data?.data?._id;
      if (isMainAsset) {
        state.application.asset.insuranceQuote = payload?.data?.data?.insuranceQuote;
      } else {
        let findindex = stateData.additionalAssets.findIndex((el) => el?._id === payload?.data?.data?._id);
        if (findindex !== -1) {
          state.application.additionalAssets[findindex].insuranceQuote = payload?.data?.data?.insuranceQuote;
        }
      }
    },
    [createInsuranceQuote.rejected]: (state, { meta }) => {
      delete state.gettingInsuranceQuote[meta.arg.assetId];
    },
    [sendInsuranceQuote.pending]: (state, action) => {
      const to = action.meta.arg?.to;
      state.sendingInsuranceQuote = {
        loading: true,
        to: to,
      };
    },
    [sendInsuranceQuote.fulfilled]: (state, { payload }) => {
      const stateData = current(state.application);
      const isMainAsset = stateData.asset._id === payload?.data?.data?._id;
      if (isMainAsset) {
        state.application.asset.insuranceQuote = payload?.data?.data?.insuranceQuote;
      } else {
        let findindex = stateData.additionalAssets.findIndex((el) => el?._id === payload?.data?.data?._id);
        if (findindex !== -1) {
          state.application.additionalAssets[findindex].insuranceQuote = payload?.data?.data?.insuranceQuote;
        }
      }
      state.sendingInsuranceQuote = {
        loading: false,
        to: "",
      };
    },
    [sendInsuranceQuote.rejected]: (state) => {
      state.sendingInsuranceQuote = {
        loading: false,
        to: "",
      };
    },
    [transferApplicationOwnership.pending]: (state) => {
      state.trasferingApplicationOwnership = true;
    },
    [transferApplicationOwnership.fulfilled]: (state, { payload }) => {
      state.application = merge(state.application, payload?.data?.data);
      state.trasferingApplicationOwnership = false;
    },
    [transferApplicationOwnership.rejected]: (state) => {
      state.trasferingApplicationOwnership = false;
    },
    [updateApplicationFollowers.pending]: (state) => {
      state.updatingApplicationFollowers = true;
    },
    [updateApplicationFollowers.fulfilled]: (state, { payload }) => {
      state.application.followers = payload?.data?.data || [];
      state.updatingApplicationFollowers = false;
      state.isOpenFollowersModal = false;
    },
    [updateApplicationFollowers.rejected]: (state) => {
      state.updatingApplicationFollowers = false;
    },
  },
});

export const {
  saveValue,
  setAssetExpandedSection,
  saveAsset,
  saveLoanDetails,
  clearState,
  updateLiabilitiesList,
  updateIncomeList,
  updateAssetList,
  updateExpensesList,
  updateAgeOfAsset,
  clearUpFinances,
  updateGlassAsset,
  saveCustomer,
  verifiedDialogBox,
  setRequiredFieldsError,
  getCurrentProductLenderId,
  openProductQuoteNoteModalForSave,
  openProductQuoteNoteModalForUpdate,
  closeProductQuoteNoteModal,
  clearProductsQuotes,
  setRequiredFieldsErrors,
  singleEntity,
  setValidationRequireFieldError,
  setCalculatorFieldsError,
  setNewCommercialCreditFile,
  setNewCustomerCreditFile,
  setNewCustomerBankStatementFile,
  setNewDocumentFile,
  setNewAssetPPSRFile,
  setNewActivityLog,
  setNewServiceUsageLog,
  updateCustomerData,
  setSubmissionModal,
  setSubmissionModalOpen,
  setSubmissionStepCompleted,
  setSubmissionSelectedDocuments,
  setIntercomChatOptions,
  updateFirstCustomersDisclaimer,
  setRemoveAssetModalOptions,
} = formdataSlice.actions;

export const store = configureStore({
  reducer: {
    userDetails: formdataSlice.reducer,
  },
});

export const stateSelector = (state) => state;
export const userDetailsSelector = (state) => state.userDetails;
export const applicationSelector = (state) => state.userDetails.application;
export const applicationCommentsSelector = (state) => state.userDetails.applicationComments;
export const activityLogSelector = (state) => state.userDetails.activityLog;
export const assetSelector = (state) => state.userDetails.application.asset;
export const assetLookupProviderSelector = (state) => state.userDetails.assetLookupProvider;
export const assetLookupProviderSettledSelector = (state) => state.userDetails.assetLookupProviderSettled;
export const loanDetailsSelector = (state) => state.userDetails.application.loanDetails;
export const entitySelector = (state) => state.userDetails.application?.entities;
export const customersSelector = (state) => state.userDetails.application.customers;
